// =============================================================================
// core/types.ts — Tipos de domínio da Serginho Autopeças
// Espelho 1:1 do modelo aplicado por core/APPLY.sql
// (core/schema.sql base v1 + core/migrations/ + core/sql/).
// Regra: qualquer mudança no SQL reflete aqui (e vice-versa).
// Dinheiro e quantidades chegam como `number`; no banco são NUMERIC.
// =============================================================================

export type UUID = string;
export type ISODate = string; // timestamptz serializado (ex.: "2026-08-03T12:00:00Z")

// --- ENUMS (espelham os CREATE TYPE do schema) -------------------------------
export type SupplierChannel = 'whatsapp' | 'email' | 'telefone' | 'portal' | 'outro';


// POStatus é da tabela `purchase_orders` da BASE v1 — nunca usada, mantida
// viva só porque o schema.sql cria índice sobre ela. O pedido de compra de
// verdade é supplier_orders (040), cuja situação é DERIVADA: ver
// SituacaoPedido na seção 6.
export type POStatus =
  | 'rascunho' | 'aprovada' | 'enviada' | 'confirmada' | 'recebida' | 'cancelada';

export type SaleChannel = 'balcao' | 'whatsapp' | 'mercadolivre' | 'amazon' | 'b2b';

export type StockMoveReason =
  | 'venda' | 'compra' | 'ajuste' | 'devolucao' | 'inventario' | 'perda';

export type EventType =
  | 'agent_run' | 'po_generated' | 'po_sent' | 'stock_moved'
  | 'price_calculated' | 'parse_result' | 'alert' | 'manual'
  | 'sale_registered' | 'cash_moved'; // v2

// v2 — caixa e papéis
export type CashFlowType = 'entrada' | 'saida';

export type PaymentMethod =
  | 'dinheiro' | 'pix' | 'debito' | 'credito' | 'boleto' | 'transferencia' | 'fiado'
  | 'a_ver'    // v6 — crédito de loja cobrindo a venda inteira
  | 'misto';   // v21 (074/075) — pagamento DIVIDIDO; as partes em sale_payments.
               // Rótulo de VENDA, nunca de dinheiro: cash_movements o recusa
               // por check, e a tela nunca o oferece como forma de pagar.

/** 087 — as bandeiras que a maquininha da loja aceita. Lista fechada, igual à
 *  do check em `taxas_de_cartao`: bandeira escrita à mão vira relatório com
 *  "Visa", "VISA" e "visa". */
export type BandeiraCartao =
  | 'visa_master' | 'elo' | 'bari_american' | 'hipercard' | 'diners';

/** 087 — o que a maquininha desconta, por bandeira e faixa de parcelas.
 *
 *  A FAIXA, e não a linha por parcela, porque é assim que a adquirente informa
 *  ("2x a 6x"), e faixa não fica com buraco no meio sem alguém notar. A venda
 *  CONGELA a taxa do dia em `sales.cartao_taxa_pct` — trocar a taxa aqui não
 *  reescreve o passado. */
export interface TaxaDeCartao {
  id: number;
  bandeira: BandeiraCartao;
  modalidade: 'debito' | 'credito';
  parcelas_de: number;
  parcelas_ate: number;
  /** Percentual sobre o valor da venda: 3.37 = 3,37%. */
  taxa_pct: number;
  vigente_desde: string;
  vigente_ate: string | null;
}

export type CashCategory =
  | 'venda' | 'compra_mercadoria' | 'frete' | 'despesa_fixa' | 'despesa_variavel'
  | 'salario' | 'imposto' | 'sangria' | 'aporte' | 'outro';

export type Papel = 'dono' | 'gerente' | 'vendedor';

// v4 — metas por unidade (migração 010)
export type GoalMetric = 'faturamento' | 'lucro_liquido' | 'qtd_vendas' | 'ticket_medio';

// --- 1. products -------------------------------------------------------------
export interface Product {
  id: UUID;
  sku: string;
  descricao: string;
  categoria: string | null;
  aplicacao: string | null;      // texto livre v1 (legado); estruturado em product_applications
  marca: string | null;
  codigo_fabrica: string | null;
  codigo_barras: string | null;
  unidade: string;               // 'un' | 'par' | 'jogo' | 'litro' ...
  ativo: boolean;
  // v2 — identificação/posição
  codigo_oem: string | null;
  /** O barras da etiqueta DO FABRICANTE (032) — não é o EAN. 4º identificador
   *  de primeira classe: busca exata, prefixo de índice e bip. */
  codigo_barras_fabricante: string | null;
  posicao: string | null;        // 'dianteira' | 'traseira' | 'esquerdo' | 'direito' ...
  // v2 — fiscal
  ncm: string | null;            // char(8)
  origem_mercadoria: number;     // 0-8 (tabela CST-origem; 0=nacional, 1=importada)
  // v2 — logística
  peso_kg: number | null;
  altura_cm: number | null;
  largura_cm: number | null;
  comprimento_cm: number | null;
  // v2 — preços (margem é CALCULADA pela RPC margens(), nunca armazenada)
  //
  // ATENÇÃO (migração 014): `preco_custo` e `preco_ecommerce` foram REVOGADOS da
  // leitura do papel `authenticated` — a RLS filtra linha e não coluna, então a
  // policy de products entregava o custo do catálogo inteiro a qualquer perfil
  // ativo. As duas colunas existem na tabela e continuam GRAVÁVEIS, mas as
  // consultas normais do app (COLUNAS_PRODUTO) não as trazem: chegam null.
  // Para lê-las use produtoFinanceiro() / obterPecaParaEdicao(), que passam pela
  // RPC com checagem de papel.
  preco_custo: number | null;    // custo de referência (por fornecedor: SupplierProduct.custo)
  preco_balcao: number | null;
  preco_ecommerce: number | null;
  /** v11 (029) — multiplicador DECIDIDO sobre o custo (1.8 = 1,8×). Sensível
   *  como o custo (balcão ÷ margem_alvo ≈ custo): revogada de authenticated,
   *  chega null nas consultas normais; leia por obterPecaParaEdicao /
   *  produtoFinanceiro. A margem REAL continua derivada, nunca armazenada. */
  margem_alvo: number | null;
  // v9 — promoções (migração 018): o "de X por Y" do balcão. null = sem
  // promoção; o check do banco exige par completo e valor abaixo do balcão.
  // O % de desconto é DERIVADO (app/src/lib/promocao.ts), nunca gravado.
  preco_promocional: number | null;
  // v2 — operação
  observacoes: string | null;    // conhecimento tácito do Serginho
  cadastro_completo: boolean;
  // v10 — similares (migração 027): peças com o mesmo uuid são a mesma peça em
  // marcas/códigos diferentes. null = sem similares. Criação em lote pela RPC
  // criar_similares; leitura direta filtrando por este campo.
  grupo_similar: UUID | null;
  // v11 — o catálogo navegável (migração 092, ADR-009)
  /** O tipo de peça (part_types.slug): "calota", "oleo_motor"... */
  tipo: string | null;
  /** Para que serve e como se usa, em português simples (não é o nome nem a nota do balcão). */
  detalhes: string | null;
  // v12 — a fila de conferência (migração 093, 06/10/2026)
  /** 'pendente' = cadastrada pelo balcão e AINDA NÃO VENDÁVEL (o banco recusa a
   *  venda, o carrinho, o orçamento, a encomenda e o pedido B2B); 'habilitado'
   *  = vende, e é o que toda peça anterior é; 'recusado' = a gerência recusou e
   *  a peça saiu do catálogo (`ativo` = false). Só as RPCs de conferencia.sql
   *  gravam: o insert e o update desta coluna são revogados de `authenticated`
   *  (rls.sql §0.1). */
  conferencia: Conferencia;
  created_at: ISODate;
  updated_at: ISODate;
}

/** Os três estados da fila de conferência (093). text + CHECK no banco, união
 *  de literais aqui — a casa não cria enum para estado operacional (regra 7).
 *
 *  Decisões do dono de 08/10/2026 (conferencia.sql): só o VENDEDOR gera
 *  'pendente' — a peça que o gerente ou o dono cadastra nasce 'habilitado';
 *  a ENTRADA DE MERCADORIA e o recebimento de pedido liberam a pendente; e
 *  só gerente e dono liberam ou recusam. */
export type Conferencia = 'pendente' | 'habilitado' | 'recusado';

/** A PAPELADA da fila (093): quem cadastrou a peça pelo balcão, o que digitou e
 *  a rasura de quem conferiu. O ESTADO não mora aqui — mora em
 *  products.conferencia, e a fila da gerência se lê de lá.
 *
 *  `original` é o que o colaborador digitou, campo a campo; `correcoes` é o
 *  de/para do que a gerência mudou ({"preco_balcao": {"de": 50, "para": 80}}).
 *  Nenhum dos dois guarda custo, margem alvo ou preço de e-commerce — e há
 *  CHECK no banco para isso: privilégio de coluna não fatia jsonb, e o autor
 *  precisa poder LER a própria rasura. */
export interface CadastroEmConferencia {
  id: UUID;
  /** Nulo quando a peça recusada foi apagada do catálogo — a linha riscada
   *  sobrevive, com o sku e a descrição congelados aqui. */
  product_id: UUID | null;
  sku: string;
  descricao: string;
  autor_id: UUID | null;
  /** Congelado, como `sales.vendedor`: continua legível depois que a pessoa sai. */
  autor_nome: string;
  original: Record<string, unknown>;
  correcoes: Record<string, { de: unknown; para: unknown }>;
  /** O porquê da recusa. Obrigatório na RPC que recusa. */
  motivo: string | null;
  conferido_por: UUID | null;
  conferido_por_nome: string | null;
  conferido_em: ISODate | null;
  created_at: ISODate;
  updated_at: ISODate;
}

/** O que o colaborador digitou no cadastro do balcão — as chaves de
 *  `original` e de `correcoes` (ficha_do_cadastro_do_balcao, conferencia.sql).
 *  `quantidade_contada` é DECLARAÇÃO: só vira saldo na liberação, e só se a
 *  peça ainda não tem saldo nem movimento — a contagem que a gerência já fez
 *  vence (habilitar_peca, 09/10/2026). Na rasura, o `para` dela é o saldo que
 *  ficou. */
export type CampoDoCadastroDoBalcao =
  | 'descricao' | 'marca' | 'tipo' | 'codigo_fabrica' | 'preco_balcao' | 'local'
  | 'quantidade_contada';

/** fila_de_conferencia(p_estado) — uma linha da fila (093, PR 2). INVOKER: a
 *  gerência recebe todas, o colaborador só as que ele cadastrou. Nada de custo
 *  nem de venda no retorno (as colunas são fixas). */
export interface LinhaDaConferencia {
  /** Nulo na recusada que o dono apagou do catálogo: a linha sobrevive, sem link. */
  product_id: UUID | null;
  sku: string;
  descricao: string;
  conferencia: Conferencia;
  ativo: boolean;
  marca: string | null;
  /** part_types.slug. */
  tipo: string | null;
  codigo_fabrica: string | null;
  preco_balcao: number | null;
  /** stock.local — o endereço normalizado (maiúsculo, sem espaço). */
  local: string | null;
  /** O saldo de verdade; na pendente do vendedor é 0 (a contagem está em
   *  original). */
  quantidade: number | null;
  /** A peça já tem saldo CONTADO: saldo diferente de 0 OU algum movimento de
   *  estoque — a MESMA régua com que habilitar_peca decide que a declaração não
   *  passa por cima. Inclui o saldo 0 contado. Na pendente, true = liberar
   *  mantém o saldo. */
  saldo_contado: boolean;
  autor_id: UUID | null;
  autor_nome: string | null;
  criado_em: ISODate;
  original: Partial<Record<CampoDoCadastroDoBalcao, string | number>>;
  /** A RASURA, de/para só do que a gerência mudou ao liberar. */
  correcoes: Partial<Record<CampoDoCadastroDoBalcao, { de: string | number | null; para: string | number | null }>>;
  motivo: string | null;
  conferido_por_nome: string | null;
  conferido_em: ISODate | null;
}

// --- 1b. product_codes (v2) — cross-reference de códigos ---------------------
export interface ProductCode {
  id: UUID;
  product_id: UUID;
  tipo: 'oem' | 'fabricante' | 'ean' | 'cross';
  valor: string;
  valor_norm: string;            // gerado no banco: upper + só [A-Z0-9]
  created_at: ISODate;
}

// --- 1c. product_applications (v2) — fitment por veículo ---------------------
export interface ProductApplication {
  id: UUID;
  product_id: UUID;
  montadora: string;             // 'Chevrolet'
  modelo: string;                // 'Celta'
  ano_inicial: number | null;
  ano_final: number | null;
  motorizacao: string | null;    // '1.0 8V'
  observacao: string | null;
  /** O modelo-base (092) — ligado pelo gatilho a partir de montadora + modelo. */
  vehicle_model_id: UUID | null;
  created_at: ISODate;
}

// --- 1c-2. O catálogo navegável (migração 092, ADR-009) ---------------------
/** Como o tipo se liga ao carro: pela peça, pelo que o carro pede, ou nenhum. */
export type ModoDoTipo = 'aplicacao' | 'especificacao' | 'universal';

export interface PartType {
  slug: string;
  nome: string;
  /** O grupo do tipo ("Lubrificantes"); vira products.categoria. */
  categoria: string;
  modo: ModoDoTipo;
  ordem: number;
  created_at: ISODate;
}

export interface VehicleModel {
  id: UUID;
  /** Grafia da FIPE: "Fiat", "GM - Chevrolet", "VW - VolksWagen". */
  montadora: string;
  nome: string;
  slug: string;
  apelidos: string[];
  created_at: ISODate;
}

export interface VehicleSpec {
  id: UUID;
  vehicle_model_id: UUID;
  tipo: string;
  geracao: string | null;
  anos: string | null;
  ano_inicial: number | null;
  ano_final: number | null;
  motor: string | null;
  valor: string;
  chaves: string[];
  litros: number | null;
  fonte: string | null;
  aprovado: boolean;
  aprovado_em: ISODate | null;
  aprovado_por: UUID | null;
  created_at: ISODate;
}

/** Um botão da navegação: tipo, montadora ou modelo, com o que há atrás dele. */
export interface ItemDaNavegacao {
  /** slug do tipo, montadora (grafia da FIPE) ou id do modelo. */
  chave: string;
  nome: string;
  /** Só nos tipos: o grupo ("Lubrificantes") e o modo. */
  grupo?: string;
  modo?: ModoDoTipo;
  ordem?: number;
  pecas: number;
  especificacoes: number;
}

/** O retorno de navegar_catalogo(): o próximo nível. */
export type NavegacaoDoCatalogo =
  | { nivel: 'inicio'; tipos: ItemDaNavegacao[]; montadoras: ItemDaNavegacao[] }
  | { nivel: 'montadoras'; modo: ModoDoTipo; sem_carro: number; itens: ItemDaNavegacao[] }
  | { nivel: 'modelos'; itens: ItemDaNavegacao[] }
  | { nivel: 'tipos'; itens: ItemDaNavegacao[] };

/** Uma especificação do carro, como pecas_para_o_carro() devolve. */
export interface EspecificacaoDoCarro {
  id: UUID;
  geracao: string | null;
  anos: string | null;
  motor: string | null;
  valor: string;
  chaves: string[];
  litros: number | null;
  fonte: string | null;
  aprovado: boolean;
}

/** Onde a peça serve, quando o carro tem mais de uma geração ou motor. */
export interface OndeServe {
  geracao: string | null;
  anos: string | null;
  motor: string | null;
  aprovado: boolean;
}

/** Uma peça para o carro (pecas_para_o_carro). */
export interface PecaParaOCarro {
  product_id: UUID;
  sku: string;
  descricao: string;
  marca: string | null;
  detalhes: string | null;
  preco_balcao: number | null;
  preco_promocional: number | null;
  quantidade: number;
  local: string | null;
  /**
   * Serve neste carro: true = em tudo o que o carro pede (no ano, ou em todas
   * as gerações sem ano); null = só em parte (`servem_em` diz onde) ou não há
   * chave para comparar ("confira no manual"); false = não bate.
   */
  casa: boolean | null;
  /** As gerações/motores cujas chaves batem com a peça (tipo por especificação). */
  servem_em: OndeServe[] | null;
  /** Tem aplicação a algum modelo — o atalho "sem carro" mostra só as que não têm. */
  tem_carro: boolean;
  caracteristicas: string | null;
  /** 093 — a peça da fila aparece aqui também (e no fim da lista); é esta
   *  coluna que faz o cartão dizer que ela ainda não vende. */
  conferencia: Conferencia;
}

export interface PecasParaOCarro {
  modo: ModoDoTipo | null;
  especificacoes: EspecificacaoDoCarro[];
  pecas: PecaParaOCarro[];
}

// --- 1d. product_attributes (migração 017) — características criadas pelo dono
//
// A característica que ninguém previu no schema: "lado", "voltagem", "rosca",
// "medida da correia". O dono cria pela tela; o cadastro monta o campo a partir
// desta lista e a busca monta as opções de filtro.
export type ProductAttributeType = 'texto' | 'lista' | 'numero';

export interface ProductAttribute {
  id: UUID;
  nome: string;
  tipo: ProductAttributeType;
  /** Só para tipo 'lista'; vazio nos demais (o banco garante nos dois sentidos). */
  opcoes: string[];
  ordem: number;
  /** Desativada some do formulário, mas o valor já gravado continua legível. */
  ativo: boolean;
  created_at: ISODate;
  updated_at: ISODate;
}

// --- 1e. product_attribute_values (017) — o valor por peça -------------------
//
// PK composta (product_id, attribute_id): uma peça tem UM valor por
// característica. `valor` é sempre texto, inclusive para tipo 'numero' — ver o
// porquê no cabeçalho da migração.
export interface ProductAttributeValue {
  product_id: UUID;
  attribute_id: UUID;
  valor: string;
  created_at: ISODate;
  updated_at: ISODate;
}

/**
 * Filtro de característica aceito por `buscar_pecas`: mapa de
 * attribute_id → valor exato. Semântica E — cada par acrescentado ESTREITA o
 * resultado. Vazio ou ausente não filtra nada.
 */
export type FiltroCaracteristicas = Record<UUID, string>;

/**
 * Características de UMA peça no cadastro: cada uma pode ter VÁRIOS valores
 * (migração 036 — a peça que serve "no 1.0 E no 1.3" tem uma linha por valor).
 * O filtro da busca continua pedindo UM valor por característica
 * (FiltroCaracteristicas): a peça casa se QUALQUER linha dela for igual.
 */
export type CaracteristicasDaPeca = Record<UUID, string[]>;

// --- 1f. product_images (migração 030) — a foto da peça ----------------------
//
// O JSON nunca carrega o binário: `dados` fica fora deste tipo de propósito.
// A foto sai pela rota /imagens/pecas/<product_id> do Caddy (pública — decisão
// documentada na migração), com ?v=<updated_at> de cache-busting. Escrita só
// por salvar_imagem_peca/remover_imagem_peca, gerente/dono, sempre JPEG.
export interface ProductImageInfo {
  /** PK própria desde a 031 (galeria) — endereça /imagens/foto/<id>. */
  id: UUID;
  product_id: UUID;
  /** A PRINCIPAL é a de menor (ordem, created_at) — posição, não flag. */
  ordem: number;
  /** Coluna gerada (octet_length) — a tela sabe o tamanho sem baixar nada. */
  bytes: number;
  largura: number | null;
  altura: number | null;
  updated_at: ISODate;
}

// --- 2. suppliers ------------------------------------------------------------
export interface Supplier {
  id: UUID;
  slug: string;             // 'skypecas' | 'roles' | 'mga' | 'dobler' | 'real-motopecas'
  nome: string;
  cidade: string | null;
  cnpj: string | null;
  canal_preferencial: SupplierChannel;
  whatsapp: string | null;  // E.164
  email: string | null;
  /** Catálogo/loja do fornecedor (migração 025). Vira link clicável; o banco só
   *  aceita http:// ou https:// — `javascript:` aqui seria XSS com o token de
   *  quem clica ao lado. */
  site: string | null;
  lead_time_dias: number;
  pedido_minimo: number;
  observacoes: string | null;
  ativo: boolean;
  created_at: ISODate;
  updated_at: ISODate;
}

// --- 3. supplier_products (ponte N:N) ---------------------------------------
export interface SupplierProduct {
  id: UUID;
  supplier_id: UUID;
  product_id: UUID;
  codigo_fornecedor: string | null;
  custo: number;
  moeda: string;            // 'BRL'
  disponivel: boolean;
  multiplo_compra: number;
  ultima_cotacao: ISODate | null;
  created_at: ISODate;
  updated_at: ISODate;
}

// --- 3b. fornecedores_da_peca() (RPC) ----------------------------------------
//
// Espelha core/sql/functions/fornecedores_peca.sql. É FUNÇÃO, e não consulta
// direta, porque supplier_products guarda `custo`, cujo SELECT é revogado de
// `authenticated` (rls.sql §2) — e privilégio de coluna derruba a consulta
// inteira, não devolve a coluna em branco.
export interface FornecedorDaPecaRow {
  supplier_id: UUID;
  nome: string;
  site: string | null;
  whatsapp: string | null;
  email: string | null;
  canal_preferencial: SupplierChannel;
  ativo: boolean;
  /** Código da peça no catálogo DELE — o que se digita no site do fornecedor. */
  codigo_fornecedor: string | null;
  /** 0 = vínculo declarado à mão, nunca cotado. A tela não mostra R$ 0,00. */
  custo: number;
  ultima_cotacao: ISODate | null;
}

// salvar_fornecedores_da_peca(uuid, jsonb) devolve integer — quantos vínculos
// ficaram gravados. Preserva custo e ultima_cotacao de quem permanece.

// --- 3c. cart_holds (migração 026) — o carrinho de balcão ---------------------
//
// A tabela é default-deny (RLS sem policy): NINGUÉM a lê ou escreve direto.
// Estes tipos espelham o retorno das RPCs de carrinho.sql.

// meu_carrinho() — o que EU tinha reservado; é o que faz o carrinho sobreviver
// a um F5. Preços VIGENTES de agora (carrinho não congela preço; documento sim).
export interface MeuCarrinhoRow {
  product_id: UUID;
  sku: string;
  descricao: string;
  quantidade: number;
  cliente_nome: string | null;
  expira_em: ISODate;
  preco_balcao: number | null;
  preco_promocional: number | null;
  disponivel: number;
  /** 093 — o carrinho renasce do banco, e com ele a régua: item de peça não
   *  conferida trava o botão de vender, em vez de a recusa aparecer só no fim. */
  conferencia: Conferencia;
}

// carrinhos_das_pecas(uuid[]) — o "Luiz reservou 2 un para o Fulano" que os
// colegas veem na busca e na ficha.
export interface CarrinhoDaPecaRow {
  product_id: UUID;
  vendedor_id: UUID;
  vendedor: string;
  quantidade: number;
  cliente_nome: string | null;
  expira_em: ISODate;
}

// segurar_carrinho(uuid, numeric, text) → timestamptz (o novo expira_em; a
// recusa por disputa vem como exceção, com o nome de quem segura).
// soltar_carrinho(uuid?) → void · renovar_carrinho(text?) → timestamptz.
// editar_a_ver(uuid, text, numeric, text, text, date, uuid) → void; a trava
// aritmética (valor >= já usado) mora na função.

// --- 4. stock / stock_movements ---------------------------------------------
export interface Stock {
  product_id: UUID;
  quantidade: number;        // saldo DOCUMENTADO — nunca negativo (migração 015)
  local: string | null;     // convenção: 'A-P3-G2' (corredor-prateleira-gaveta)
  estoque_min: number;
  estoque_max: number;
  reorder_point: number;
  reservado: number;
  // v7 (migração 015) — unidades vendidas ANTES de a entrada ser registrada
  // (dívida de registro, >= 0). Entrada de mercadoria abate; inventário zera.
  pendente_registro: number;
  updated_at: ISODate;
}

/** Um lugar EXTRA da peça (migração 092): a calota que fica no gancho e também
 *  no chão. O principal continua em Stock.local. Escrita só pelas RPCs
 *  acrescentar_lugar_da_peca / tirar_lugar_da_peca (dono e gerente). */
export interface StockLugar {
  product_id: UUID;
  /** Maiúsculo e sem espaço, como o mapa casa. */
  local: string;
  created_at: ISODate;
}

export interface StockMovement {
  id: UUID;
  product_id: UUID;
  delta: number;            // + entrada / - saída
  reason: StockMoveReason;
  ref_table: string | null;
  ref_id: UUID | null;
  saldo_apos: number | null;
  observacao: string | null;
  created_at: ISODate;
}

// --- 5. sales / sale_items ---------------------------------------------------
export interface Sale {
  id: UUID;
  canal: SaleChannel;
  /** Nome de quem vendeu, congelado no momento da venda (sobrevive a exclusão). */
  vendedor: string | null;
  /** Nome do cliente CONGELADO na venda. Continua existindo depois da migração
   *  023 e continua sendo a verdade do histórico: cadastro corrigido depois não
   *  reescreve o passado. Ver `customer_id` para quem é hoje. */
  cliente: string | null;
  /** v9 (migração 023) — cliente cadastrado. Nulo nas vendas anteriores e nas
   *  que o balcão fechou sem identificar ninguém. */
  customer_id: UUID | null;
  /** 073 — a montadora do carro desta venda, congelada aqui SEMPRE na grafia
   *  da FIPE ("VW - VolksWagen", "GM - Chevrolet", "Fiat") — a mesma de
   *  vehicle_models e de montadora_canonica(). Até 08/10/2026 vinha de uma
   *  lista; desde então o vendedor digita e a tela lê (app/src/lib/carro.ts),
   *  mas a montadora nunca é texto livre. Congelada pelo mesmo motivo de
   *  `cliente`: recarregar o catálogo não pode reescrever a venda de março.
   *  Nula quando o vendedor não informou o carro. */
  veiculo_montadora: string | null;
  /** 073 — o modelo, congelado junto da montadora. Desde 08/10/2026 é o
   *  NOME-BASE + o que foi digitado ("Gol 1.0", "Uno Mille", "Kombi 1.4"); as
   *  vendas de 14/09 a 08/10 guardam a versão da FIPE ("Gol (novo) 1.0 Mi
   *  Total Flex 8V 4p") ou a grafia da lista "com peça na loja" ("FIAT" /
   *  "UNO"), e ficam como estão. Quem lê agrupa todas pelo modelo-base:
   *  modelo_base_de() e montadora_canonica() (092), na hora de ler.
   *  O banco garante que os dois andam juntos (check `sales_veiculo_completo`). */
  veiculo_modelo: string | null;
  /** 073 — o ano, opcional mesmo quando há modelo (a FIPE não traz ano em
   *  vehicle_catalog; quem digita é o vendedor). Entre 1950 e 2100. */
  veiculo_ano: number | null;
  /** 073 — qual carro CADASTRADO do cliente (051) esta venda atendeu: o "quem
   *  é hoje" ao lado do par congelado acima, como customer_id acompanha
   *  cliente. Nulo na venda de balcão sem cliente, que é a maioria. */
  customer_vehicle_id: UUID | null;
  total: number;
  observacao: string | null;
  /** 087 — a bandeira do cartão desta venda. Nula fora do cartão, e nula nas
   *  vendas anteriores à migração. Os três andam juntos (check no banco). */
  cartao_bandeira: BandeiraCartao | null;
  /** 087 — em quantas vezes. 1 = à vista; débito é sempre 1. */
  cartao_parcelas: number | null;
  /** 087 — a taxa que a maquininha desconta, CONGELADA no dia da venda.
   *  Congelada, e não lida da tabela na hora de exibir: trocar a taxa não pode
   *  reescrever o que já foi vendido. O LÍQUIDO não existe como coluna
   *  (regra 4) — é `total × (1 − taxa/100)`, calculado onde se lê. */
  cartao_taxa_pct: number | null;
  /** v2. 'misto' (075) = pagamento DIVIDIDO: as partes estão em
   *  sale_payments, e é de lá que o caixa foi lançado, uma entrada por parte. */
  forma_pagamento: PaymentMethod | null;
  desconto: number;                      // v2
  // v3 — autoria e cancelamento (migração 008)
  vendedor_id: UUID | null;              // FK profiles; base do escopo por vendedor na RLS
  cancelada_em: ISODate | null;          // null = venda válida
  cancelada_por: UUID | null;
  motivo_cancelamento: string | null;
  vendida_em: ISODate;
  /**
   * v7 (migração 014) — chave de idempotência da tentativa de venda. Índice
   * único parcial no banco: duplo clique no balcão, ou reenvio da requisição
   * por rede instável, devolve a MESMA venda em vez de baixar o estoque e
   * lançar o caixa duas vezes. Nulo nas vendas anteriores à migração.
   */
  idempotency_key: string | null;
  // v15 (migração 061) — o número do RECIBO e a entrega.
  /** 'VD-000001', gerado. É o que o cliente traz de volta no papel. */
  numero: number;
  codigo: string;
  /** Esta venda vai ser ENTREGUE. Não é canal: balcão também se entrega. */
  entrega: boolean;
  /** CONGELADO na venda — cliente que muda de casa não reescreve a entrega
   *  de ontem. O cadastro (customers.endereco) é só o padrão da tela. */
  entrega_endereco: string | null;
  entrega_contato: string | null;
  entrega_observacao: string | null;
  /** Como o cliente vai pagar NA PORTA (só na venda fiada). */
  entrega_forma_prevista: PaymentMethod | null;
  /** "Levar troco para R$ X" — o entregador não sai sem trocado. */
  entrega_troco_para: number | null;
  /** A taxa de entrega (064). FORA de total de propósito: total é peça. */
  entrega_taxa: number;
  /** total + entrega_taxa — GERADA no banco, não escreva. É o que o cliente
   *  paga, e o que foi para o caixa. */
  total_cobrado: number;
  // v20 (migração 070) — A CORREÇÃO DO CONTEÚDO, pelo dono. Número e código
  // NUNCA mudam: o recibo que o cliente levou continua achando esta venda.
  /** Em que versão o conteúdo está. 1 = nunca corrigida. */
  versao: number;
  /** Quando a última correção aconteceu; null = como foi registrada. */
  corrigida_em: ISODate | null;
  corrigida_por: UUID | null;
  /** O porquê da última correção — obrigatório na RPC. */
  motivo_correcao: string | null;
  entregue_em: ISODate | null;
  entregue_por: UUID | null;
  entregue_por_nome: string | null;
  // v14 (migração 055) — a venda que PASSOU PELO CAIXA. Conferir é opcional:
  // nem toda venda vai ao balcão do dono, e nulo aqui não é pendência.
  conferida_em: ISODate | null;
  conferida_por: UUID | null;
  conferida_por_nome: string | null;
  /** O que o cliente entregou em dinheiro; o troco é isto menos o total. */
  valor_recebido: number | null;
  created_at: ISODate;
}

/**
 * Uma PARTE do pagamento dividido (migração 075): "R$ 100 em dinheiro" de uma
 * venda cuja forma é 'misto'. Só a venda dividida tem linhas aqui — a de uma
 * forma só continua sem nada (guardar uma parte para toda venda seria número
 * derivado, regra 4). As partes somam total_cobrado − a_ver_usado; a forma
 * nunca é fiado, a_ver ou misto (check no banco). Escrita só por
 * registrar_venda; leitura com o recorte de sale_items (RLS §19).
 */
export interface SalePayment {
  id: UUID;
  sale_id: UUID;
  forma_pagamento: PaymentMethod;
  valor: number;
  created_at: ISODate;
}

// --- 4b. limites_operacao (v7) — tetos de desconto e de vale, por papel ------
// Linha única (id = 1). Existe para que o teto seja DADO editável pelo dono e
// não número mágico dentro da RPC: mudar a política da loja não exige deploy.
// Quem faz valer é registrar_venda/emitir_orcamento/emitir_a_ver, no banco — a
// tela só mostra o limite para o vendedor não descobrir na frente do cliente.
export interface LimitesOperacao {
  id: 1;
  desconto_max_pct_vendedor: number;
  desconto_max_pct_gerente: number;
  a_ver_max_gerente: number;
  /** Piso de preço: % do preco_balcao abaixo do qual a venda é recusada. */
  preco_min_pct_do_balcao: number;
  /** Maior sobra de vale que o VENDEDOR pode abonar ao encerrar um vale na
   *  venda (migração 034). Gerente e dono não têm teto. */
  abono_sobra_max_vendedor: number;
  /** PEÇA DIVERSA (048): preço sugerido = custo digitado × este número.
   *  Default 2 — a prática da casa. Editável só pelo dono. */
  coringa_multiplicador: number;
  /** O que o dono investiu além do que o sistema enxerga (043). Nulo = não
   *  informado; o ROIC sai só sobre estoque + contas, com aviso na tela. */
  capital_investido: number | null;
  /** Alíquota de SIMULAÇÃO de imposto sobre a receita (043). Nulo = sem
   *  simulação. O imposto pago de verdade é o do caixa. */
  aliquota_imposto_pct: number | null;
  atualizado_em: ISODate;
  atualizado_por: UUID | null;
}

// --- 4c. limites_por_pessoa (migração 069) — o teto da PESSOA ---------------
//
// `limites_operacao` acima é a régua do PAPEL: todo vendedor dá 10%, todo
// gerente 25%. Esta é a EXCEÇÃO nomeada — o vendedor antigo que pode 20%
// enquanto o recém-chegado segue em 10. Sem ela, o dono só tinha como
// afrouxar a loja inteira ou promover a gerente quem não é.
//
// NULO EM CADA COLUNA = "esta pessoa segue o teto do papel". Não é o valor do
// papel copiado: quem não tem ajuste acompanha a política quando ela muda.
//
// Tabela separada de `profiles` de propósito — a policy upd_profiles deixa o
// GERENTE editar a linha do vendedor, e um teto de desconto ali dentro lhe
// daria o poder de mandar zerar uma venda. Escrita só por
// definir_limites_da_pessoa (dono, com motivo e evento).
export interface LimitesPorPessoa {
  profile_id: UUID;
  /** Teto de desconto em % do bruto. Nulo = segue o papel. */
  desconto_max_pct: number | null;
  /** Piso de preço em % do vigente. Nulo = segue o papel. */
  preco_min_pct_do_balcao: number | null;
  /** Maior "a ver" emitido de uma vez. Nulo = segue o papel. Só gerente
   *  emite vale, e a RPC recusa gravar isto para vendedor. */
  a_ver_max: number | null;
  /** Maior sobra de vale abonada na venda (034). Nulo = segue o papel —
   *  e para gerente "segue o papel" significa SEM teto. */
  abono_sobra_max: number | null;
  /** O porquê do ajuste. Obrigatório enquanto houver ajuste. */
  observacao: string | null;
  atualizado_em: ISODate;
  atualizado_por: UUID | null;
}

// O retorno de limites_efetivos() — os tetos JÁ resolvidos (pessoa, senão
// papel). É o que a tela usa para mostrar o número ANTES de o vendedor
// prometer ao cliente, e é a mesma função que as RPCs chamam para recusar:
// duas contas separadas é como a tela e o banco passam a discordar.
//
// NULO AQUI SIGNIFICA "SEM TETO" — o oposto do nulo em LimitesPorPessoa acima.
// A exceção é `desconto_max_pct`, que nunca vem nulo: o dono sai com 100,
// porque desconto maior que o bruto da venda não é isenção, é conta errada.
export interface LimitesEfetivos {
  profile_id: UUID;
  papel: Papel;
  desconto_max_pct: number;
  preco_min_pct_do_balcao: number | null;
  a_ver_max: number | null;
  abono_sobra_max: number | null;
  /** Algum dos quatro tetos veio de ajuste próprio, e não do papel. */
  personalizado: boolean;
}

/**
 * O conteúdo ANTERIOR de uma venda corrigida (070). Uma linha por versão
 * substituída — o snapshot é inteiro e burro de propósito: reconstruir a venda
 * de duas correções atrás aplicando diferenças seria um caminho que erra em
 * silêncio, e é numa auditoria que se precisa confiar nele.
 */
export interface SaleVersao {
  id: UUID;
  sale_id: UUID;
  /** Qual versão esta linha guarda (1 = como a venda nasceu). */
  versao: number;
  total: number;
  desconto: number;
  entrega_taxa: number;
  forma_pagamento: PaymentMethod | null;
  cliente: string | null;
  customer_id: UUID | null;
  observacao: string | null;
  /** As peças da versão, congeladas: {product_id, descricao, quantidade,
   *  preco_unit, custo_unit}. Não é FK para sale_items — aquelas linhas são
   *  apagadas na correção, e o histórico não pode depender delas. */
  itens: Array<{
    product_id: UUID;
    descricao: string | null;
    quantidade: number;
    preco_unit: number;
    custo_unit: number | null;
  }>;
  corrigida_em: ISODate;
  corrigida_por: UUID | null;
  corrigida_por_nome: string | null;
  motivo: string;
}

export interface SaleItem {
  id: UUID;
  sale_id: UUID;
  product_id: UUID;
  quantidade: number;
  preco_unit: number;
  /** ATENÇÃO (28/09/2026): `custo_unit` foi REVOGADO de `authenticated`, como
   *  products.preco_custo. A coluna existe e continua gravada pela venda, mas
   *  consulta normal do app não a traz — chega null, e pedi-la explicitamente
   *  derruba a consulta inteira com 42501. É o custo CONGELADO da peça naquela
   *  venda: com preco_unit ao lado, a margem daquela peça sairia por subtração,
   *  e faturamento/margem deixaram de ser vista de funcionário. Quem pode ver
   *  lê por função definer (CMV, indicadores do dono, correção de venda). */
  custo_unit: number | null;
  subtotal: number;         // gerado no banco (quantidade * preco_unit)
  /** PEÇA DIVERSA (041): o que REALMENTE saiu nesta linha. Nulo na peça
   *  cadastrada, cuja descrição é a do catálogo. É o texto que vai para o
   *  recibo do cliente. */
  descricao_avulsa: string | null;
  /** PEÇA DIVERSA (071): o código do fabricante daquela peça, digitado no
   *  balcão. USO INTERNO — recompra, similar e futuro cadastro. NUNCA sai em
   *  documento de cliente; a trava está nos testes de app/src/lib/*Doc.test.ts. */
  codigo_avulso: string | null;
  /** 073 — o carro desta LINHA, quando ela é de um carro DIFERENTE do carro da
   *  venda. NULO QUER DIZER "o carro da venda": quem lê faz
   *  `coalesce(si.veiculo_montadora, s.veiculo_montadora)`. Ao contrário de
   *  `codigo_avulso`, vale para qualquer linha — peça cadastrada também é de
   *  um carro. Os dois campos andam juntos (check no banco). Mesmo formato do
   *  carro da venda (Sale.veiculo_montadora / veiculo_modelo); sem ano, que a
   *  linha não tem — digitado, o número fica dentro do modelo. */
  veiculo_montadora: string | null;
  veiculo_modelo: string | null;
  created_at: ISODate;
}

// --- 5a2. As duas portas da GERÊNCIA para a venda do colega (28/09/2026) -----
//
// core/sql/functions/venda_para_gerencia.sql. As duas são `security definer`
// com exige_papel(['gerente','dono']) no CORPO, devolvem UMA venda por chamada
// e não têm irmã "listar": a 090 tirou a venda do colega da lista do gerente,
// mas não tirou dele o trabalho de cancelar, devolver e cobrar o fiado dela.
// Nenhuma das duas devolve `custo_unit`, nem soma, média ou contagem.

/** Uma linha de itens_da_venda_para_gerencia(sale_id): as colunas de
 *  `sale_items` MENOS custo_unit, mais as cinco de `products` que o embed da
 *  tela trazia — achatadas. O app remonta o objeto `products`. As cinco vêm
 *  nulas JUNTAS quando não há linha de produto. */
export interface ItemDaVendaParaGerencia {
  id: UUID;
  quantidade: number;
  preco_unit: number;
  subtotal: number;
  product_id: UUID;
  descricao_avulsa: string | null;
  codigo_avulso: string | null;
  veiculo_montadora: string | null;
  veiculo_modelo: string | null;
  sku: string | null;
  descricao: string | null;
  marca: string | null;
  observacoes: string | null;
  codigo_oem: string | null;
  codigo_fabrica: string | null;
}

/** A venda achada por venda_pelo_codigo(codigo) — o gesto do balcão, com a
 *  notinha na mão ('VD-000123' ou '123'; a normalização é do banco). Zero
 *  linhas = não existe venda com esse código, e isso não é erro.
 *
 *  O QUE NÃO ESTÁ AQUI é decisão, não esquecimento: a conferência de caixa
 *  (conferida_*, valor_recebido) porque o caixa é só do dono desde 28/09/2026;
 *  o histórico de correção (versao, corrigida_em, motivo_correcao) porque a 090
 *  levou sales_versoes inteiro para o dono (o jsonb de itens carrega o custo
 *  congelado); e as partes do pagamento dividido, que o §19 do rls.sql só dá
 *  ao gerente nas vendas dele. */
export interface VendaPeloCodigo {
  id: UUID;
  codigo: string;
  vendida_em: ISODate;
  cliente: string | null;
  customer_id: UUID | null;
  vendedor: string | null;
  vendedor_id: UUID | null;
  total: number;
  desconto: number;
  forma_pagamento: PaymentMethod | null;
  observacao: string | null;
  cancelada_em: ISODate | null;
  motivo_cancelamento: string | null;
  entrega: boolean;
  entrega_endereco: string | null;
  entrega_contato: string | null;
  entrega_observacao: string | null;
  entrega_forma_prevista: PaymentMethod | null;
  entrega_troco_para: number | null;
  entregue_em: ISODate | null;
  entregue_por_nome: string | null;
  entrega_taxa: number;
  total_cobrado: number;
  veiculo_montadora: string | null;
  veiculo_modelo: string | null;
  veiculo_ano: number | null;
  /** O quanto de vale "a ver" (012) cobriu esta venda. O cancelamento devolve
   *  o crédito, e é por isso que ele desce junto. */
  a_ver_usado: number;
}

/** Uma linha de `vendas_para_gerencia(...)` — a LISTA das vendas de todo mundo
 *  para quem gerencia (07/10/2026, pedido do dono: "que ele visualize o valor
 *  de cada venda para documentar, mas não o valor total das vendas somadas").
 *
 *  É a mesma venda de `VendaPeloCodigo`, com três diferenças deliberadas:
 *  TRAZ `versao`/`corrigida_em`/`motivo_correcao` (são colunas de `sales`, e
 *  venda corrigida tem de parecer corrigida para quem lê a lista de outra
 *  pessoa); traz a conferência de caixa SÓ nas vendas de quem chamou (nas dos
 *  colegas as duas voltam nulas, e o caixa segue do dono); e traz as peças e as
 *  partes do pagamento em jsonb, porque a lista filtra por nome de peça e por
 *  forma. Nenhuma soma, em lugar nenhum — nem aqui, nem na função. */
export interface VendaParaGerencia extends VendaPeloCodigo {
  versao: number;
  corrigida_em: ISODate | null;
  motivo_correcao: string | null;
  /** Nulo quando a venda é de outra pessoa — ver o cabeçalho. */
  conferida_em: ISODate | null;
  conferida_por_nome: string | null;
  /** Só o que ACHA a venda: nome, sku e o código digitado na peça diversa.
   *  Sem preço e sem quantidade — o dinheiro por peça continua entrando pela
   *  `itens_da_venda_para_gerencia`, quando a sanfona abre. */
  itens: Array<{
    descricao_avulsa: string | null;
    codigo_avulso: string | null;
    sku: string | null;
    descricao: string | null;
  }>;
  pagamentos: Array<{ forma_pagamento: PaymentMethod; valor: number }>;
}

// --- 5b. devolucoes / devolucao_itens (migração 033) — troca e estorno -------
//
// A devolução PARCIAL de uma venda. A venda original fica intacta; sku e
// descrição dos itens são CONGELADOS no ato (exceção por contrato da regra 4).
// modo 'estorno' = dinheiro saiu do caixa; 'vale' = virou "a ver" para troca.
// Leitura: gerência (RLS). Escrita: só registrar_devolucao.
export type DevolucaoModo = 'estorno' | 'vale';

export interface Devolucao {
  id: UUID;
  sale_id: UUID;
  modo: DevolucaoModo;
  valor: number;
  forma_pagamento: PaymentMethod | null;  // só no estorno
  credit_id: UUID | null;                 // só na troca (o vale que nasceu)
  motivo: string;
  registrado_por: UUID | null;
  registrado_por_nome: string | null;     // congelado, como sales.vendedor
  created_at: ISODate;
}

export interface DevolucaoItem {
  id: UUID;
  devolucao_id: UUID;
  sale_item_id: UUID;
  product_id: UUID;
  sku: string;                            // congelado no ato
  descricao: string;                      // congelado no ato
  quantidade: number;
  preco_unit: number;
  subtotal: number;
  created_at: ISODate;
}

// registrar_devolucao(uuid, jsonb, text, text, payment_method) → uma linha:
export interface RegistrarDevolucaoRow {
  devolucao_id: UUID;
  /** O valor TOTAL devolvido — a soma das peças que voltaram. */
  valor: number;
  credit_id: UUID | null;
  codigo: string | null;                  // o código do vale, quando troca
  /**
   * Quanto desse valor abateu o fiado em aberto da venda (065): quem deve e
   * devolve a peça para de dever aquele valor. Só a sobra virou dinheiro ou
   * vale — por isso o `codigo` pode vir nulo mesmo no modo troca.
   */
  abatido_no_fiado: number;
}

// --- 6. Pedidos de compra ao FORNECEDOR (migração 040) -----------------------
//
// NÃO confundir com `reservations` (020), que é a encomenda de um CLIENTE.
// A situação do pedido é DERIVADA dos itens (situacao_do_pedido) e nunca
// digitada; o total também é derivado. O recebimento é EVENTO — um pedido
// pode chegar em três levas, e a quantidade recebida é a soma delas.
//
// As tabelas homônimas da base v1 (com supplier_id/status/origem) eram
// carcaça de um desenho nunca construído e foram derrubadas pela 040.

export type SituacaoPedido =
  | 'rascunho' | 'enviado' | 'recebido_parcial' | 'recebido' | 'cancelado';
export type CanalPedido =
  | 'whatsapp' | 'telefone' | 'portal' | 'representante' | 'balcao';
export type TipoItemPedido = 'compra' | 'garantia' | 'bonificacao';

/** Uma linha de listar_pedidos(): o pedido com o que já é derivado dele. */
export interface PedidoResumo {
  id: UUID;
  numero: number;
  fornecedor_id: UUID;
  fornecedor: string;
  data_pedido: ISODate;
  previsao_entrega: ISODate | null;
  confirmado_em: ISODate | null;
  situacao: SituacaoPedido;
  canal: CanalPedido;
  numero_no_fornecedor: string | null;
  condicao_pagamento: string | null;
  /** Soma dos itens + frete − desconto. Derivado, nunca armazenado. */
  valor_pedido: number;
  valor_frete: number;
  desconto: number;
  itens: number;
  /** Quantos itens ainda não chegaram (saldo cancelado não conta). */
  itens_pendentes: number;
  /** Peças de encomenda que já chegaram e o cliente ainda não sabe. */
  encomendas_a_avisar: number;
  observacoes: string | null;
  criado_por_nome: string | null;
  cancelado_em: ISODate | null;
}

export interface PedidoItem {
  id: UUID;
  supplier_order_id: UUID;
  /** Nullable: a loja pede peça que ainda não está cadastrada. */
  product_id: UUID | null;
  /** Congelados — é documento trocado com o fornecedor. */
  descricao: string;
  codigo_fabricante: string | null;
  quantidade: number;
  custo_unitario: number;
  tipo: TipoItemPedido;
  /** O fornecedor avisou que o resto não vem — é o que deixa o pedido fechar. */
  saldo_cancelado: boolean;
  encomenda_cliente_nome: string | null;
  encomenda_cliente_telefone: string | null;
  cliente_avisado: boolean;
  avisado_em: ISODate | null;
  observacao: string | null;
  created_at: ISODate;
}

export interface PedidoRecebimento {
  id: UUID;
  supplier_order_id: UUID;
  recebido_em: ISODate;
  nf_numero: string | null;
  nf_serie: string | null;
  /** 44 dígitos. Guardado para consultar no portal — não se lê o XML aqui. */
  nf_chave: string | null;
  nf_valor_total: number | null;
  recebido_por: UUID | null;
  recebido_por_nome: string | null;
  observacao: string | null;
  created_at: ISODate;
}

// --- 7. price_rules (Fase 3) -------------------------------------------------
export interface PriceRule {
  id: UUID;
  escopo: 'sku' | 'categoria' | 'fornecedor' | 'global';
  alvo: string | null;
  markup_percent: number | null;
  margem_alvo: number | null;
  preco_min: number | null;
  prioridade: number;       // menor = maior prioridade
  ativo: boolean;
  created_at: ISODate;
  updated_at: ISODate;
}

// --- 8. events (append-only) -------------------------------------------------
export interface Event {
  id: UUID;
  type: EventType;
  actor: string;
  ref_table: string | null;
  ref_id: UUID | null;
  payload: Record<string, unknown>;
  created_at: ISODate;
}

// --- 9. cash_movements (v2) — livro-razão do caixa ---------------------------
export interface CashMovement {
  id: UUID;
  tipo: CashFlowType;
  categoria: CashCategory;
  forma_pagamento: PaymentMethod | null;
  valor: number;                 // sempre > 0; direção vem de `tipo`
  descricao: string | null;
  ref_table: string | null;
  ref_id: UUID | null;
  ocorrido_em: ISODate;
  registrado_por: UUID | null;   // profiles.id
  created_at: ISODate;
}

// --- 10. profiles (v2) — papéis dos funcionários -----------------------------
// Dados NÃO sensíveis da equipe: gerente e dono leem. Salário e comissão ficam
// em employment_terms (só dono) — RLS filtra linha, não coluna.
export interface Profile {
  id: UUID;                      // = auth.users.id (Supabase)
  nome: string;
  papel: Papel;
  ativo: boolean;                // false = acesso cortado (papel_atual() → null)
  // v3 (migração 008)
  email: string | null;          // cópia de auth.users.email (cliente não lê o schema auth)
  telefone: string | null;
  admitido_em: string | null;    // 'YYYY-MM-DD'
  desligado_em: string | null;
  // v4 (migração 010) — unidade da pessoa; carimba vendas e caixa via trigger
  unit_id: UUID | null;
  // migração 072 — a cor desta pessoa no mural. Nulo = o dono ainda não
  // escolheu, e a tela usa uma cor provisória estável (ver CorDaEquipe).
  cor: CorDaEquipe | null;
  created_at: ISODate;
}

// --- 10b. units (v4) — unidades/lojas. Hoje 1 (Matriz); metas são por unidade.
export interface Unit {
  id: UUID;
  slug: string;                  // 'matriz'
  nome: string;
  cidade: string | null;
  ativo: boolean;
  created_at: ISODate;
}

// --- 10c. goals (v4) — metas trimestrais por unidade. Escrita: dono. ---------
// Guarda SÓ o alvo; o realizado é calculado por metas_progresso() — número
// derivado nunca se armazena (mesmo invariante da RPC margens()).
export interface Goal {
  id: UUID;
  unit_id: UUID;
  ano: number;
  trimestre: 1 | 2 | 3 | 4;
  metrica: GoalMetric;
  valor_meta: number;
  observacao: string | null;
  criado_por: UUID | null;
  created_at: ISODate;
  updated_at: ISODate;
}

// metas_progresso(ano, trimestre?) — meta × realizado, filtrada por papel no
// servidor: SÓ O DONO recebe linhas desde 28/09/2026. Antes o recorte escondia
// apenas a métrica de lucro, e a meta de faturamento do trimestre — alvo e
// realizado, em R$ — descia para vendedor e gerente. Quem não é dono recebe
// VAZIO, não erro (a tela sumiu junto).
export interface MetaProgressoRow {
  goal_id: UUID;
  unit_id: UUID;
  unit_nome: string;
  ano: number;
  trimestre: number;
  metrica: GoalMetric;
  valor_meta: number;
  realizado: number;
  atingido_pct: number;
}

// --- 10d. store_credits (v6) — "a ver": crédito de loja -----------------------
// Documento assinado, não negociável e não transferível. Emissão não toca o
// caixa (o dinheiro da venda original já entrou); no resgate, entra no caixa
// só o que o cliente pagar além do crédito.
/** O vocabulário fechado do "a ver" (058) — a correção de escopo em forma de
 *  tipo: o balcão emitia vale por encomenda por falta de papel de encomenda. */
export type OrigemAVer = 'devolucao' | 'troca' | 'acordo';

export const ORIGENS_A_VER: ReadonlyArray<{ id: OrigemAVer; rotulo: string; ajuda: string }> = [
  { id: 'devolucao', rotulo: 'Devolução', ajuda: 'O cliente trouxe a peça de volta e ficou com crédito.' },
  { id: 'troca', rotulo: 'Troca', ajuda: 'Trocou por outra peça e sobrou valor.' },
  { id: 'acordo', rotulo: 'Acordo', ajuda: 'Cortesia, ajuste de preço combinado, garantia resolvida em crédito.' },
];

export interface StoreCredit {
  id: UUID;
  numero: number;
  codigo: string;                // 'AV-000001' — gerado, imutável
  unit_id: UUID | null;
  /** CONGELADO na emissão: é o nome que o cliente assinou no papel. Corrigir o
   *  cadastro depois não reescreve documento assinado. */
  cliente_nome: string;
  cliente_doc: string | null;
  /** Cliente cadastrado, quando houver (migração 024). Responde "quem é hoje";
   *  quem responde "o que está no papel" é cliente_nome. Vale transcrito de
   *  caderno antigo costuma vir sem vínculo. */
  customer_id: UUID | null;
  valor_original: number;
  saldo: number;                 // pode ser gasto aos poucos
  motivo: string | null;
  /** De onde nasceu o crédito (058). NULA nos vales anteriores à regra.
   *  ENCOMENDA NÃO ESTÁ NA LISTA de propósito: encomenda gera encomenda. */
  origem: OrigemAVer | null;
  product_id: UUID | null;       // peça devolvida, se identificada
  valido_ate: string | null;     // 'YYYY-MM-DD'; null = sem vencimento
  emitido_por: UUID | null;
  /** Quando o crédito NASCEU (migração 021) — data do papel, se transcrito.
   *  created_at segue sendo quando entrou no sistema; no fluxo normal coincidem. */
  emitido_em: ISODate;
  cancelado_em: ISODate | null;
  cancelado_por: UUID | null;
  motivo_cancelamento: string | null;
  created_at: ISODate;
}

// Ledger de resgate: cada uso aponta para a venda que o consumiu.
export interface StoreCreditUse {
  id: UUID;
  credit_id: UUID;
  sale_id: UUID;
  valor: number;
  abono: boolean;                // sobra que o cliente abriu mão ao encerrar o vale (034)
  estornado_em: ISODate | null;  // venda cancelada devolve o saldo
  created_at: ISODate;
}

// --- 10e-pre. reservations (migração 020) — encomendas do balcão -------------
// O PEDIDO do cliente, com prazo. Não baixa estoque: o elo com a prateleira é
// reservado_da_peca(). Todos veem; mexe quem separou, ou a gerência (§14).
//
// 066: as PEÇAS saíram daqui e viraram reservation_items — uma encomenda é
// uma lista, e o total dela é a soma dos itens (total_da_encomenda), nunca uma
// coluna. Por isso `product_id`, `quantidade` e `valor_total` não existem mais.
//
// 067: e o PEDIDO passa a apontar para o cliente cadastrado (customer_id), como
// a venda (023) e o vale (024) já apontavam.

export type ReservationStatus = 'ativa' | 'atendida' | 'cancelada';

export interface Reservation {
  id: UUID;
  cliente_nome: string;
  cliente_contato: string | null;
  /** Cliente cadastrado (067), quando o balcão atrelou um. Nulo é a maioria e
   *  continua válido: encomenda de quem passou uma vez. `cliente_nome` segue
   *  sendo o nome CONGELADO no papel — o vínculo diz quem é hoje, o nome diz o
   *  que foi prometido. converter_encomenda_em_venda() o repassa à venda. */
  customer_id: UUID | null;
  vendedor_id: UUID;
  /** Congelado (mesma exceção de sales.vendedor): legível após desligamento. */
  vendedor: string;
  observacao: string | null;
  /** 'EN-000001' (058) — o número do papel que o cliente traz de volta. */
  numero: number;
  codigo: string;
  expira_em: string;             // 'YYYY-MM-DD' — depois disso, expirada (derivado)
  status: ReservationStatus;
  // --- O recado (050): "se eu não estiver, alguém sabe".
  /** Para quem é o recado; NULL com aviso_em preenchido = todos. */
  aviso_para: UUID | null;
  aviso_texto: string | null;
  /** NULL = encomenda sem recado. */
  aviso_em: ISODate | null;
  aviso_de: UUID | null;
  /** Nome congelado de quem deixou o recado. */
  aviso_de_nome: string | null;
  // --- O dinheiro da encomenda (056). A 054 gravava `pago boolean`; virou a
  // soma dos pagamentos, que é derivada e não se guarda (regra 4). O COMBINADO
  // seguiu o mesmo caminho na 066: é a soma dos itens, não uma coluna.
  /** A venda que esta encomenda virou. Nasce SOZINHA quando o pagamento fecha
   *  a conta (068) — as duas coisas convivem: a encomenda continua na lista
   *  até o cliente levar a peça (`status`), e a venda já está no histórico.
   *  Nulo com a conta fechada = conversão recusada (o motivo está em `events`)
   *  ou encomenda anterior à 068; é quando a tela oferece "Registrar a venda". */
  sale_id: UUID | null;
  created_at: ISODate;
  updated_at: ISODate;
}

/**
 * Uma PEÇA da encomenda (066). A encomenda virou lista: cada linha com a sua
 * quantidade, o seu valor e — na peça diversa — o nome que o cliente lê no
 * papel. É o espelho de sale_items.descricao_avulsa (041), uma etapa antes.
 */
export interface ReservationItem {
  id: UUID;
  reservation_id: UUID;
  product_id: UUID;
  quantidade: number;
  /** O combinado DESTA LINHA (não o preço unitário). Nulo = a acertar — e
   *  enquanto houver uma assim, a encomenda inteira fica "a combinar". */
  valor: number | null;
  /** PEÇA DIVERSA (9999): o que o cliente pediu. Obrigatório nela (trigger) e
   *  sempre nulo na peça cadastrada, cuja descrição é a do catálogo. */
  descricao_avulsa: string | null;
  /** PEÇA DIVERSA (071): o código, de uso interno — nunca no papel que o
   *  cliente leva para retirar. Espelha sale_items.codigo_avulso. */
  codigo_avulso: string | null;
  created_at: ISODate;
}

/** Um pagamento da encomenda (056): sinal, parcela ou quitação. Cada um lança
 *  no caixa na hora — o que a 054 deliberadamente não fazia. O dinheiro é do
 *  PEDIDO, não da peça: o cliente deixa sinal do atendimento (066). */
export interface ReservationPayment {
  id: UUID;
  reservation_id: UUID;
  valor: number;
  forma: PaymentMethod;
  observacao: string | null;
  /** O lançamento no livro. Nulo só nas linhas migradas da 054. */
  cash_movement_id: UUID | null;
  recebido_em: ISODate;
  recebido_por: UUID | null;
  recebido_por_nome: string | null;
  /** Estorno é soft e tem contrapartida no caixa; a linha não some. */
  estornado_em: ISODate | null;
  estornado_por: UUID | null;
  estornado_por_nome: string | null;
  created_at: ISODate;
}

// --- 10d-bis. customers (migração 023) — clientes da loja -------------------
// Cadastro aberto a todo perfil ativo (o vendedor cadastra no ato da venda);
// editar é gerente/dono; o histórico de compras sai por historico_do_cliente().
// CPF é opcional — no balcão, exigir documento faria o cadastro não acontecer —
// mas é ÚNICO quando existe, senão o histórico se parte em dois.

export interface Customer {
  id: UUID;
  nome: string;
  /** Como o balcão digitou. A unicidade é sobre a forma normalizada (norm_code). */
  cpf: string | null;
  telefone: string | null;
  email: string | null;
  /** Endereço de entrega padrão (061). A entrega congela o dela na venda. */
  endereco: string | null;
  observacao: string | null;
  ativo: boolean;
  criado_por: UUID | null;
  created_at: ISODate;
  updated_at: ISODate;
}

// buscar_clientes(q, limite) — o seletor do balcão. `compras`/`ultima_compra`
// saem da policy de sales: o vendedor vê a contagem DELE, a gerência vê tudo.
export interface BuscarClientesRow {
  id: UUID;
  nome: string;
  cpf: string | null;
  telefone: string | null;
  ativo: boolean;
  compras: number;
  ultima_compra: ISODate | null;
}

// historico_do_cliente(id, de, ate) — definer, SÓ DONO desde 28/09/2026 (o
// que o cliente comprou, e por quanto, é faturamento).
export interface HistoricoClienteRow {
  sale_id: UUID;
  vendida_em: ISODate;
  total: number;
  desconto: number;
  forma_pagamento: PaymentMethod | null;
  vendedor: string | null;
  cancelada_em: ISODate | null;
  itens: number;
}

// --- 10e-bis2. print_jobs (migração 022) — fila de impressão ----------------
// A tela enfileira o documento PRONTO; o agente da VPS entrega (hoje pelo
// e-mail da impressora). Existe porque os computadores do balcão têm internet
// mas não alcançam a impressora, e window.print() imprime sempre pela fila do
// sistema operacional de quem clicou. Leitura gerente/dono (o documento carrega
// dado do cliente) — rls.sql §15.

export type PrintJobStatus = 'fila' | 'enviado' | 'falha' | 'cancelado';

export interface PrintJob {
  id: UUID;
  // 045: orçamento imprime na loja por qualquer papel; 058: a encomenda
  // entra pela mesma régua — quem atende o balcão imprime o papel dela.
  tipo: 'a_ver' | 'orcamento' | 'encomenda' | 'relatorio' | 'recibo';
  ref_table: string | null;
  ref_id: UUID | null;
  titulo: string;
  /** Documento autocontido, gravado na emissão — não é regerado na entrega.
   *
   *  ATENÇÃO (28/09/2026): REVOGADO de `authenticated`. O documento pronto é o
   *  recibo/relatório INTEIRO (com total de venda), e lê-lo daqui contornaria
   *  a policy de vendas. Quem imprime de verdade é o agente da loja, com a
   *  chave de serviço; a fila continua legível (título, status, tentativas).
   *  Consulta de app NUNCA pede esta coluna. */
  documento_html: string;
  status: PrintJobStatus;
  tentativas: number;
  erro: string | null;
  solicitante_id: UUID | null;
  solicitante_nome: string;
  created_at: ISODate;
  enviado_em: ISODate | null;
}

// --- 10e. vehicle_plates (v6) — placa → veículo, base própria do balcão ------
export interface VehiclePlate {
  placa: string;                 // normalizada: AAA9999 ou AAA9A99 (Mercosul)
  montadora: string;
  modelo: string;
  ano: number | null;
  motorizacao: string | null;
  /**
   * SOMENTE característica técnica do veículo (motor trocado, roda aro 15).
   * PROIBIDO nome, telefone, CPF ou qualquer dado de pessoa — LGPD. A restrição
   * está documentada também como `comment on column` na migração 014, para
   * sobreviver à rotatividade da equipe.
   */
  observacao: string | null;
  // v7 (migração 014) — de onde veio o registro. 'balcao' = alguém viu o carro;
  // 'api' = consulta externa, que é palpite do órgão e pode precisar de correção.
  origem: 'balcao' | 'api';
  cilindrada: string | null;
  // v8 (migração 076) — o que a consulta externa disse, recortado por lista de
  // campos permitidos em infra/agente/placa.mjs. null na placa identificada só
  // no balcão, e nas consultadas antes de 18/09/2026 (a resposta era jogada fora).
  consulta_api: ConsultaDaPlaca | null;
  criado_por: UUID | null;
  created_at: ISODate;
  updated_at: ISODate;
}

/**
 * vehicle_plates.consulta_api — dado de ORIGEM: o que o fornecedor respondeu no
 * dia em que o crédito foi gasto. `dados` conserva os NOMES de campo do
 * fornecedor (MARCA, anoModelo, extra.cilindradas…) porque é sobre eles que o
 * agente refaz a leitura das versões FIPE; a tela os traduz em rótulos.
 * Todo valor chega como texto aparado — vazio não entra.
 */
export interface ConsultaDaPlaca {
  fornecedor: 'apiplacas';
  consultada_em: ISODate;
  dados: DadosDaConsultaDePlaca;
}

export interface DadosDaConsultaDePlaca {
  MARCA?: string;
  marca?: string;
  MODELO?: string;
  modelo?: string;
  SUBMODELO?: string;
  VERSAO?: string;
  ano?: string;
  anoModelo?: string;
  cor?: string;
  origem?: string;               // "NACIONAL" / "IMPORTADO" — procedência, não a origem do registro
  extra?: {
    ano_fabricacao?: string;
    ano_modelo?: string;
    cilindradas?: string;        // em cm³, como o Denatran escreve: "1984"
    combustivel?: string;
    tipo_veiculo?: string;
    especie?: string;
    segmento?: string;
    sub_segmento?: string;
    tipo_carroceria?: string;
    quantidade_passageiro?: string;
    eixos?: string;
    nacionalidade?: string;
  };
  fipe?: { dados: VersaoFipeDaConsulta[] };
}

/** Uma versão da tabela FIPE que a consulta sugeriu. Código = `${codigo_marca}-${codigo_modelo}`,
 *  a mesma chave de vehicle_catalog.codigo_fipe. */
export interface VersaoFipeDaConsulta {
  codigo_marca: string;
  codigo_modelo: string;
  texto_modelo: string;
  texto_marca?: string;
  ano_modelo?: string;
  combustivel?: string;
  texto_valor?: string;          // "R$ 28.799,00", como a FIPE publica
  mes_referencia?: string;       // "maio de 2022"
}

// --- 10e-bis. vehicle_catalog (v7) — universo de veículos, fonte FIPE --------
// Alimenta o AUTOCOMPLETE do cadastro de aplicação, para que montadora e modelo
// virem lista fechada em vez de texto livre ('VW' vs 'Volks' vs 'Volkswagen').
// NÃO é a fonte da cascata de busca: a cascata continua mostrando só o que a
// loja tem. Populado por scripts/fipe-sync.mjs.
export interface VehicleCatalog {
  codigo_fipe: string;
  montadora: string;
  modelo: string;
  ano: number | null;
  combustivel: string | null;
  atualizado_em: ISODate;
}

// --- 10f. store_zones (v6) — mapa da loja em coordenadas reais (cm) ----------
export interface StoreZone {
  id: UUID;
  unit_id: UUID | null;
  codigo: string;                // 'A', 'A-P3', 'BALCAO' — casa com stock.local
  nome: string | null;
  tipo: 'estante' | 'area' | 'parede' | 'porta' | 'balcao';
  x_cm: number;
  y_cm: number;
  largura_cm: number;
  altura_cm: number;
  ordem: number;
  created_at: ISODate;
}

// opcoes_busca_guiada() — próximos níveis disponíveis no catálogo
export interface OpcoesBuscaGuiada {
  /** O que a LOJA tem peça cadastrada (product_applications). */
  montadoras: string[];
  modelos: string[];
  ano_min: number | null;
  ano_max: number | null;
  motorizacoes: string[];
  categorias: string[];
  /** O universo FIPE (vehicle_catalog) — v2. A tela mostra em grupo separado:
   *  o vendedor navega sempre, sem a lista prometer peça que a loja não tem. */
  montadoras_catalogo: string[];
  /** Modelos do universo, só depois de escolhida a montadora. */
  modelos_catalogo: string[];
}

// --- 10g. quotes (v7) — orçamento de balcão ----------------------------------
// Documento com os valores das peças para o cliente levar. Não mexe em estoque
// nem caixa; sku/descrição/preços ficam congelados na emissão. Vira venda por
// converter_orcamento() dentro da validade — vencido, emite-se outro.
// --- Promoções com vigência (migração 047) -----------------------------------
// O % é DECISÃO digitada; preco_de/preco_por são o contrato congelado que o
// histórico mostra. products.preco_promocional segue sendo o carimbo que as
// telas consomem — a vassoura aplicar_promocoes() mantém os dois em dia.

export interface Promotion {
  id: UUID;
  product_id: UUID;
  desconto_pct: number;
  preco_de: number;
  preco_por: number;
  inicio: ISODate;   // 'YYYY-MM-DD'
  fim: ISODate;      // 'YYYY-MM-DD'
  /** Quando saiu de vigor (encerrada à mão ou varrida após o fim). Aberta = null. */
  encerrada_em: ISODate | null;
  criado_por: UUID | null;
  criado_por_nome: string | null;
  created_at: ISODate;
}

/**
 * Um carro do cliente (051). A frota da locadora, os dois carros da família,
 * o pátio da concessionária — cada linha é um vínculo, não o carro em si:
 * carro troca de dono, e é o vínculo que termina (`ativo`).
 */
export interface CustomerVehicle {
  id: UUID;
  customer_id: UUID;
  /** MAIÚSCULA e sem hífen (a RPC normaliza). Nula quando não se sabe. */
  placa: string | null;
  montadora: string | null;
  modelo: string | null;
  ano: number | null;
  motorizacao: string | null;
  /** Como a loja chama: "o Volvo do pátio". Numa frota de 12, é o que salva. */
  apelido: string | null;
  observacao: string | null;
  ativo: boolean;
  criado_por: UUID | null;
  created_at: ISODate;
  updated_at: ISODate;
}

/** De onde veio a cotação (049). Text+check no banco — canal novo é migração
 *  de uma linha, sem enum fora de transação. */
export type CanalOrcamento = 'balcao' | 'whatsapp' | 'telefone';

export interface Quote {
  id: UUID;
  numero: number;
  codigo: string;                 // 'OR-000001' — gerado, imutável
  unit_id: UUID | null;
  cliente_nome: string | null;    // opcional: orçamento rápido de balcão
  cliente_contato: string | null; // fone/WhatsApp p/ reenviar o documento
  vendedor: string | null;        // nome congelado (mesmo padrão de sales)
  vendedor_id: UUID | null;
  desconto: number;               // desconto geral em R$ (como em sales)
  total: number;                  // soma dos itens - desconto, congelado na emissão
  valido_ate: string;             // 'YYYY-MM-DD' — sempre presente (padrão: +7 dias)
  observacao: string | null;
  /** 049 — o meio pelo qual o cliente cotou. Default 'balcao'. */
  canal: CanalOrcamento;
  /** 049 — o carro como o cliente falou ("Gol 2012 1.0 8v"). */
  veiculo: string | null;
  /** 051 — o cliente CADASTRADO (recorrência/prospecção). */
  customer_id: UUID | null;
  /** 051 — qual carro dele esta cotação atende. */
  customer_vehicle_id: UUID | null;
  sale_id: UUID | null;           // preenchido quando virou venda
  convertido_em: ISODate | null;  // null = ainda não virou venda
  created_at: ISODate;
  updated_at: ISODate;
}

export interface QuoteItem {
  id: UUID;
  quote_id: UUID;
  product_id: UUID | null;        // set null se a peça sair do catálogo
  sku: string;                    // congelado na emissão
  descricao: string;              // congelada na emissão
  quantidade: number;
  preco_unit: number;             // preço prometido ao cliente
  preco_tabela: number | null;    // preco_balcao do momento ("de X por Y")
  ordem: number;                  // ordem em que o vendedor montou o documento
  subtotal: number;               // gerado no banco (quantidade * preco_unit)
  /** PEÇA DIVERSA (071): o código, congelado com o orçamento e invisível no
   *  papel e no PDF do cliente — como o `sku` já era desde 18/08/2026. */
  codigo_avulso: string | null;
  created_at: ISODate;
}

// emitir_orcamento(p_itens, ...) — itens de entrada no formato RegistrarVendaItem;
// linha devolvida pela RPC:
export interface OrcamentoEmitidoRow {
  quote_id: UUID;
  codigo: string;
  valido_ate: string;             // 'YYYY-MM-DD'
}

// --- 11. employment_terms (v3) — combinado vigente. SÓ DONO. -----------------
export interface EmploymentTerms {
  profile_id: UUID;
  salario_base: number;
  comissao_percent: number;      // 0-100, aplicado sobre o vendido no mês
  /** O dia do mês em que ESTA pessoa recebe (083). Decisão digitada: a
   *  admissão não serve para deduzir. Padrão 5, o teto legal. */
  dia_pagamento: number;
  observacao: string | null;
  updated_at: ISODate;
}

// --- 12. payroll_entries (v3) — folha por mês. SÓ DONO. ----------------------
export interface PayrollEntry {
  id: UUID;
  profile_id: UUID;
  competencia: string;           // 'YYYY-MM-01' (sempre dia 1º, garantido por CHECK)
  /** O dia em que este lançamento vai ser pago (083). Vem do dia_pagamento
   *  do termo e pode ser ajustado no mês. Nulo = não se sabe, e aí o fluxo
   *  de caixa usa a competência. */
  vencimento: ISODate | null;
  salario_base: number;
  comissao: number;
  bonus: number;
  descontos: number;
  total: number;                 // gerado no banco
  observacao: string | null;
  /** timestamptz (não é data pura como contas_pagar.pago_em): o dia da loja
   *  sai dele pelo fuso, nunca pelo fatiamento. null = em aberto. */
  pago_em: ISODate | null;
  forma_pagamento: PaymentMethod | null;
  cash_movement_id: UUID | null; // lançamento de saída gerado por pagar_folha()
  /** true = nasceu do combinado por gerar_folha_prevista() e ninguém mexeu
   *  (094). Só estas acompanham salário e dia do termo. Salvar no Painel
   *  grava false. */
  prevista: boolean;
  created_at: ISODate;
  updated_at: ISODate;
}

// --- VIEWS de apoio ----------------------------------------------------------
// v_reposicao — itens abaixo do ponto de pedido (input do restock-agent).
export interface ReposicaoRow {
  product_id: UUID;
  sku: string;
  descricao: string;
  categoria: string | null;
  em_estoque: number;
  reorder_point: number;
  estoque_max: number;
  sugerir_comprar: number;
  // v7 (migração 015): item com pendência entra na fila mesmo acima do ponto
  // de pedido — dívida de registro é prioridade de conferência.
  pendente_registro: number;
}

// melhor_fornecedor() — menor custo disponível por produto.
//
// NÃO É MAIS VIEW. Nasceu como `v_melhor_fornecedor` na base v1 e foi
// DERRUBADA: ela expõe `supplier_products.custo`, revogado de `authenticated`
// na migração 014. Como view invoker passaria a falhar para todo mundo (dono e
// vendedor compartilham o papel de banco); como definer virava erro de lint.
// Virou função com checagem de papel em core/sql/functions/financeiro.sql — o
// mesmo padrão de produto_financeiro() e margens(). O schema.sql ainda a cria
// (é a base v1 congelada) e o financeiro.sql a derruba no mesmo APPLY.
export interface MelhorFornecedorRow {
  product_id: UUID;
  supplier_id: UUID;
  supplier_slug: string;
  supplier_nome: string;
  custo: number;
  codigo_fornecedor: string | null;
  lead_time_dias: number;
  pedido_minimo: number;
}

// v_caixa_diario / v_caixa_mensal (v2)
export interface CaixaPeriodoRow {
  entradas: number;
  saidas: number;
  saldo: number;
  lancamentos: number;
}
export interface CaixaDiarioRow extends CaixaPeriodoRow { dia: ISODate }
export interface CaixaMensalRow extends CaixaPeriodoRow { mes: ISODate }
export interface CaixaPorHoraRow {
  dia: ISODate;
  hora: number;                  // 0-23
  entradas: number;
  saidas: number;
  lancamentos: number;
}

// v_vendas_coringa (core/sql/views/coringa.sql) — fila de auditoria do SKU
// 9999 (migração 016). Herda a RLS de sales; `auditada` = venda cancelada
// (o acerto é cancelar e relançar com o SKU correto).
export interface VendaCoringaRow {
  sale_id: UUID;
  vendida_em: ISODate;
  vendedor: string | null;
  quantidade: number;
  preco_unit: number;
  valor: number;
  observacao: string | null;
  auditada: boolean;
}

// --- VIEWS v3 (core/sql/views/equipe.sql) ------------------------------------
// v_vendas_por_vendedor — base da comissão. SÓ O DONO desde 28/09/2026: além
// de herdar a RLS de sales, a view tem `papel_atual() = 'dono'` no corpo, porque
// "quanto eu vendi no mês" pré-somado é exatamente o agregado que saiu da vista
// do funcionário. Venda cancelada não entra.
export interface VendasPorVendedorRow {
  competencia: string;           // 'YYYY-MM-01'
  vendedor_id: UUID | null;
  vendedor_nome: string | null;
  vendas: number;
  total_vendido: number;
  ticket_medio: number;
}

// v_folha_mensal — consolidado da folha por mês. Só dono.
export interface FolhaMensalRow {
  competencia: string;
  funcionarios: number;
  salarios: number;
  comissoes: number;
  bonus: number;
  descontos: number;
  total: number;
  pago: number;
  em_aberto: number;
}

// v_custos_mensais — para onde o dinheiro foi, por mês e categoria.
export interface CustosMensaisRow {
  competencia: string;
  categoria: CashCategory;
  lancamentos: number;
  total: number;
}

// v_resultado_mensal — entrou, saiu, sobrou.
export interface ResultadoMensalRow {
  competencia: string;
  receitas: number;
  custos: number;
  folha: number;
  mercadoria: number;
  resultado: number;
}

// margens() (v7) — RPC, não view: lê coluna revogada e checa papel dentro
export interface MargensRow {
  product_id: UUID;
  sku: string;
  descricao: string;
  categoria: string | null;
  preco_custo: number | null;
  preco_balcao: number | null;
  preco_ecommerce: number | null;
  margem_balcao_pct: number | null;
  margem_ecommerce_pct: number | null;
}

// --- RPCs (v2) — contratos das funções security definer ----------------------
// buscar_pecas(q, limite) — busca universal (código sujo, EAN, descrição, veículo)
export interface BuscarPecasRow {
  product_id: UUID;
  sku: string;
  descricao: string;
  marca: string | null;
  categoria: string | null;
  posicao: string | null;
  codigo_oem: string | null;
  codigo_fabrica: string | null;
  codigo_barras: string | null;
  codigo_barras_fabricante: string | null;
  quantidade: number;
  localizacao: string | null;
  estoque_min: number;
  preco_balcao: number | null;
  preco_promocional: number | null;  // migração 018 — o cartão rasura o balcão
  aplicacoes: string | null;     // 'Chevrolet Celta 2010-2015 | Fiat Palio ...'
  observacoes: string | null;    // notas do balcão, em letra miúda no resultado
  grupo_similar: UUID | null;    // migração 027 — chave dos similares no cartão
  /** 093 — a peça da fila APARECE na busca (decisão do dono); é esta coluna que
   *  faz o cartão dizer que ela ainda não vende. */
  conferencia: Conferencia;
  relevancia: number;            // 0..1
}

// produto_financeiro(p_product_id) — custo e margem de UMA peça.
// Definer com checagem de papel dentro: quem não pode ver recebe erro 42501, e
// o app trata isso como "esconde o bloco", não como falha.
export interface ProdutoFinanceiroRow {
  preco_custo: number | null;
  preco_ecommerce: number | null;
  /** A DECIDIDA (coluna, migração 029) — compare com a calculada abaixo. */
  margem_alvo: number | null;
  /** Calculada na função, nunca armazenada (regra 4). */
  margem_balcao_pct: number | null;
}

// emitir_a_ver(...) — o vale recém-criado. O `codigo` (AV-000123) é o que vai
// impresso no papel e o que o cliente informa no balcão para resgatar.
export interface EmitirAVerRow {
  credit_id: UUID;
  codigo: string;
}

// --- RPCs que devolvem ESCALAR ------------------------------------------------
// Não têm interface porque não têm forma: o contrato é o tipo devolvido, e está
// anotado aqui para o espelho 1:1 do cabeçalho continuar verdadeiro.
//
//   registrar_venda(...)        -> uuid      (o id da venda criada)
//   converter_orcamento(...)    -> uuid      (o id da venda gerada)
//   enfileirar_impressao(...)   -> uuid      (o id do trabalho na fila)
//   reservado_da_peca(uuid)     -> numeric   (quanto está prometido)
//   saida_do_deploy(uuid)       -> text      (o log do build)
//   montadoras_catalogo()       -> setof text
//   modelos_catalogo(text)      -> setof text

// registrar_venda(p_itens, ...) — item do array jsonb de entrada
export interface RegistrarVendaItem {
  product_id: UUID;
  quantidade: number;
  preco_unit: number;
  /** PEÇA DIVERSA (041): o que saiu nesta linha. Só o coringa a guarda. */
  descricao?: string;
  /** PEÇA DIVERSA (048): o custo digitado, que vira o CMV da linha. */
  custo?: number;
  /** PEÇA DIVERSA (071): o código do fabricante, de uso interno. Só o coringa
   *  o guarda — peça cadastrada tem os códigos dela no catálogo, e aceitar um
   *  aqui criaria uma segunda verdade sobre a mesma peça. */
  codigo?: string;
  /** 073 — o carro desta linha, quando é OUTRO carro que não o da venda.
   *  Omitir (ou mandar nulo) quer dizer "o carro da venda". Diferente dos três
   *  acima, NÃO é exclusivo do coringa. Os dois vêm juntos ou nenhum vem: a
   *  RPC recusa meio carro. */
  veiculo_montadora?: string;
  veiculo_modelo?: string;
}

/** registrar_venda(..., p_pagamentos) — uma PARTE do pagamento dividido (075).
 *  Só com p_forma = 'misto': duas ou mais, em formas de dinheiro (nem fiado,
 *  nem a_ver, nem misto), somando exatamente o que a venda cobra. */
export interface PartePagamento {
  forma: PaymentMethod;
  valor: number;
}

// entrada_mercadoria(p_itens, ...) — item do array jsonb de entrada
export interface EntradaMercadoriaItem {
  product_id: UUID;
  quantidade: number;
  custo_unit?: number;
}

// --- OBSERVABILIDADE (migração 019) ------------------------------------------
// As três tabelas que a aba Sistema lê. Escrita só por service_role (o agente
// da VPS); leitura só pelo dono — ver rls.sql §12.

/** Estado de uma publicação. `abandonado` = o agente morreu no meio. */
export type DeployStatus = 'rodando' | 'sucesso' | 'falha' | 'abandonado';

export interface Deploy {
  id: UUID;
  iniciado_em: ISODate;
  concluido_em: ISODate | null;
  solicitante_id: UUID | null;
  /** Congelado no momento do deploy — sobrevive ao desligamento da pessoa. */
  solicitante_nome: string;
  commit_antes: string | null;
  commit_depois: string | null;
  commit_assunto: string | null;
  /** Etapa corrente enquanto status = 'rodando'; null depois. */
  etapa: string | null;
  status: DeployStatus;
  duracao_ms: number | null;
  /** Saída do build, truncada em 64 kB pelo agente. */
  saida: string | null;
  erro: string | null;
}

/** Acesso HTTP servido pelo Caddy. `ip_prefixo` é /24, nunca o IP cheio (LGPD). */
export interface AccessLog {
  id: number;
  em: ISODate;
  host: string;
  metodo: string;
  caminho: string;
  status: number;
  duracao_ms: number;
  bytes: number;
  ip_prefixo: string | null;
  user_agent: string | null;
  referer: string | null;
  criado_em: ISODate;
}

export interface ContainerEstado {
  nome: string;
  estado: string;              // 'running', 'exited', ...
  saude: string | null;        // 'healthy' | 'unhealthy' | null (sem healthcheck)
  subiu_em: ISODate | null;
}

export interface VpsAmostra {
  em: ISODate;
  cpu_pct: number | null;
  carga_1m: number | null;
  mem_total_mb: number | null;
  mem_usada_mb: number | null;
  disco_total_gb: number | null;
  disco_usado_gb: number | null;
  /** Dump mais recente em infra/backups/. `null` = nunca houve backup. */
  backup_em: ISODate | null;
  backup_bytes: number | null;
  containers: ContainerEstado[];
}

// --- RPCs de observabilidade — contratos de leitura (dono) --------------------

/** saude_sistema() — UMA linha com o que muda a decisão do dia. */
export interface SaudeSistema {
  amostra_em: ISODate | null;
  cpu_pct: number | null;
  mem_usada_mb: number | null;
  mem_total_mb: number | null;
  disco_usado_gb: number | null;
  disco_total_gb: number | null;
  containers: ContainerEstado[] | null;
  /** `null` = nenhum dump existe. A tela trata isso como alerta, não como "—". */
  backup_em: ISODate | null;
  backup_bytes: number | null;
  ultimo_deploy_em: ISODate | null;
  ultimo_deploy_status: DeployStatus | null;
  ultimo_deploy_commit: string | null;
  deploy_rodando: boolean;
  acessos_24h: number;
  erros_24h: number;
  p95_ms_24h: number | null;
  vendas_hoje: number;
  faturamento_hoje: number;
}

/** metricas_negocio() — cobertura do catálogo, dívidas operacionais, movimento. */
export interface MetricasNegocio {
  produtos_ativos: number;
  cadastro_completo: number;
  com_custo: number;
  com_preco_balcao: number;
  com_local: number;
  pendente_registro: number;
  coringa_pendente: number;
  sem_estoque: number;
  abaixo_do_minimo: number;
  vendas_30d: number;
  faturamento_30d: number;
  ticket_medio_30d: number;
  canceladas_30d: number;
}

export interface AcessosPorDiaRow {
  dia: string;                 // date serializado 'YYYY-MM-DD'
  acessos: number;
  erros: number;
  /** Aproximação sem cookie: pares distintos de prefixo de IP + user agent. */
  visitantes: number;
  p95_ms: number | null;
}

export interface AcessosPorRotaRow {
  host: string;
  caminho: string;
  acessos: number;
  erros: number;
  media_ms: number;
  ultimo_em: ISODate;
}

export interface ErroRecenteRow {
  em: ISODate;
  host: string;
  metodo: string;
  caminho: string;
  status: number;
  duracao_ms: number;
  referer: string | null;
}

export interface VpsSerieRow {
  em: ISODate;
  cpu_pct: number | null;
  carga_1m: number | null;
  mem_usada_mb: number | null;
  mem_total_mb: number | null;
  disco_usado_gb: number | null;
  disco_total_gb: number | null;
}

/** listar_deploys() — o histórico sem a coluna `saida` (que pode ter 64 kB). */
export type DeployResumo = Omit<Deploy, 'saida' | 'solicitante_id'>;

// --- O AGENTE DA VPS (infra/agente/) — respostas HTTP, não do Postgres --------
//
// As duas rotas que a aba Sistema chama por `fetch` em vez de PostgREST, porque
// o que elas devolvem não está no banco: `/status` mede a máquina AGORA (quem
// abre essa tela está desconfiando de alguma coisa neste minuto, e um minuto de
// atraso é justamente a diferença que importa) e `/deploy` dispara um processo.
// Autenticação: `Bearer <access_token>` do dono — o agente revalida o papel no
// banco, com o token de quem pediu.

/** GET /infra/v1/status — a amostra ao vivo, irmã de VpsAmostra. */
export interface AgenteStatus {
  em: ISODate;
  cpu_pct: number | null;
  carga_1m: number | null;
  mem_total_mb: number | null;
  mem_usada_mb: number | null;
  disco_total_gb: number | null;
  disco_usado_gb: number | null;
  backup_em: ISODate | null;
  backup_bytes: number | null;
  containers: ContainerEstado[];
  /**
   * O UUID do deploy em andamento, ou null. CUIDADO: em `SaudeSistema` o campo
   * de mesmo nome é BOOLEAN — lá é "existe algum rodando?" (vem de um `exists`
   * no SQL), aqui é "qual". A tela usa este para acompanhar o progresso.
   */
  deploy_rodando: UUID | null;
}

/** POST /infra/v1/deploy — 202 com o id, ou 409 quando já há um rodando. */
export interface AgenteDeployAceito {
  id: UUID;
}

// --- Contrato comum dos agentes (ARCHITECTURE.md §4) -------------------------
// Entrada tipada -> 1 regra de negócio -> saída tipada + evento logado.
export interface AgentResult<T> {
  ok: boolean;
  data: T;
  events: Array<Pick<Event, 'type' | 'actor' | 'ref_table' | 'ref_id' | 'payload'>>;
  warnings?: string[];
}

// --- 20. Agenda da equipe (migração 038) -------------------------------------
//
// A REGRA da tarefa é `agenda_tarefas`; as OCORRÊNCIAS são expandidas na
// leitura por agenda_do_periodo() e nunca existem como linha. O escopo
// ('loja' | 'pessoal' | 'conjunta') é DERIVADO da lista de participantes —
// lista vazia é a rotina da loja, que vale para todos. Ver o cabeçalho da
// migração para o porquê de a recorrência não ser materializada aqui (ao
// contrário das contas a pagar, onde a ocorrência é editável e pagável).

export type AgendaRecorrencia = 'nenhuma' | 'diaria' | 'semanal' | 'mensal';
export type AgendaPrioridade  = 'baixa' | 'normal' | 'alta';
export type AgendaEscopo      = 'loja' | 'pessoal' | 'conjunta';

export interface AgendaTarefa {
  id: UUID;
  titulo: string;
  descricao: string | null;
  data_inicio: ISODate;
  data_fim: ISODate | null;
  /** 'HH:MM:SS' — nula significa "durante o dia", como a maioria das rotinas. */
  hora: string | null;
  duracao_min: number | null;
  recorrencia: AgendaRecorrencia;
  /** 0=domingo … 6=sábado — a mesma convenção de extract(dow) e de getDay(). */
  dias_semana: number[];
  dia_mes: number | null;
  prioridade: AgendaPrioridade;
  ativa: boolean;
  criado_por: UUID | null;
  criado_por_nome: string | null;
  created_at: ISODate;
  updated_at: ISODate;
}

/** Uma linha de agenda_do_periodo(): a tarefa NUM DIA. */
export interface AgendaOcorrencia {
  tarefa_id: UUID;
  dia: ISODate;
  titulo: string;
  descricao: string | null;
  hora: string | null;
  duracao_min: number | null;
  prioridade: AgendaPrioridade;
  recorrencia: AgendaRecorrencia;
  escopo: AgendaEscopo;
  participantes: Array<{ id: UUID; nome: string }>;
  concluida_em: ISODate | null;
  criado_por_nome: string | null;
}

// --- 21. Contas a pagar (migração 039) ---------------------------------------
//
// A REGRA (ContaRecorrente) e as OCORRÊNCIAS (ContaPagar). Ao contrário da
// agenda, a ocorrência aqui É linha real: ela é editável e pagável — a luz veio
// R$ 412, o aluguel atrasou. `valor_real` nulo significa "ainda é estimativa".

export type CategoriaConta =
  | 'aluguel' | 'energia' | 'agua' | 'internet' | 'telefone'
  | 'salarios' | 'pro_labore' | 'contador' | 'impostos'
  | 'taxa_cartao' | 'seguro' | 'alvara' | 'fornecedores'
  | 'manutencao' | 'outros'
  // 081: o que sai do caixa e NÃO é da loja (conta pessoal, financiamento
  // de terceiro, o dinheiro tirado para outros fins). Vira 'sangria' no
  // caixa, e a DRE a exclui das despesas: retirada é destino do lucro.
  | 'retirada';

export interface ContaRecorrente {
  id: UUID;
  descricao: string;
  categoria: CategoriaConta;
  /** NULO desde a 046 = valor variável (luz): a regra materializa a DATA. */
  valor_previsto: number | null;
  /** 1–31; dia 31 cai no último dia dos meses curtos. */
  dia_vencimento: number;
  data_inicio: ISODate;
  data_fim: ISODate | null;
  fornecedor_id: UUID | null;
  ativa: boolean;
  observacao: string | null;
  criado_por: UUID | null;
  created_at: ISODate;
  updated_at: ISODate;
}

/** Qual anexo da conta (080): o documento que COBRA (boleto, conta de
 *  consumo, guia, nota) ou o que prova o PAGAMENTO. Uma vaga de cada. */
export type TipoDeAnexo = 'boleto' | 'comprovante';

/** Metadados de um anexo (044) — o binário NUNCA vem na lista; sai só
 *  pela RPC obter_comprovante_conta, em base64, para o dono. */
export interface ComprovanteMeta {
  content_type: 'image/jpeg' | 'application/pdf';
  bytes: number;
  nome: string | null;
}

/** Uma linha de contas_pagar_comprovantes SEM o binário: as colunas que a
 *  lista pode embutir. `dados` fica de fora de propósito — o arquivo sai só
 *  pela RPC, e na lista pesaria megabytes por conta. */
export interface AnexoDaConta extends ComprovanteMeta {
  conta_id: UUID;
  tipo: TipoDeAnexo;
  created_at: ISODate;
}

export interface ContaPagar {
  id: UUID;
  descricao: string;
  categoria: CategoriaConta;
  /** NULO (046) = conta de regra variável, aguardando a fatura. */
  valor_previsto: number | null;
  /** Nulo = ainda é estimativa (a tela mostra com ~ e em itálico). */
  valor_real: number | null;
  vencimento: ISODate;
  recorrencia_id: UUID | null;
  fornecedor_id: UUID | null;
  pago_em: ISODate | null;
  cash_movement_id: UUID | null;
  /** Quem deu a baixa (041). Congelado: sobrevive ao desfazer do pagamento. */
  pago_por: UUID | null;
  pago_por_nome: string | null;
  forma_pagamento: PaymentMethod | null;
  observacao: string | null;
  criado_por: UUID | null;
  created_at: ISODate;
  updated_at: ISODate;
  /** Embutido pela lista (044). Nulo = sem comprovante anexado. */
  comprovante?: ComprovanteMeta | null;
  /** O boleto (080): a outra vaga de anexo. Nulo = sem boleto anexado. */
  boleto?: ComprovanteMeta | null;
  /** Pedido de compra que gerou a conta (040) — estava na tabela desde
   *  sempre e faltava no tipo (dívida catalogada em 23/08/2026). */
  supplier_order_id: UUID | null;
  /** Linha digitável do boleto (077), só números: 47 no boleto de banco, 48
   *  no convênio. Nulo = sem código. Grava por salvar_codigo_barras_conta. */
  codigo_barras: string | null;
  /** CANCELADA (086), não apagada: o documento é a única cópia no sistema, e
   *  `excluir_conta` o levaria junto por cascade. Nulo = conta válida.
   *
   *  TODA leitura de contas_pagar precisa de `cancelada_em is null` — esquecer
   *  não dá erro, dá número errado. */
  cancelada_em: ISODate | null;
  cancelada_por: UUID | null;
  /** Congelado, como em pago_por_nome. */
  cancelada_por_nome: string | null;
  /** Obrigatório quando há cancelamento: sem motivo é buraco no histórico. */
  motivo_cancelamento: string | null;
  /** De qual importação de PDF esta conta nasceu (086). */
  lote_id: number | null;
  /** Compra paga NA HORA ao fornecedor (095): nasceu já paga, pela tela
   *  "Paguei fornecedor" (registrar_compra_na_hora). Desfaz-se apagando
   *  (desfazer_compra_na_hora), nunca devolvendo para "A pagar".
   *
   *  No banco é `not null default false`. Opcional AQUI de propósito: tornar
   *  obrigatório quebraria no typecheck todo fixture que monta um ContaPagar
   *  inteiro — inclusive os de PR aberto em paralelo, que o CI de nenhum dos
   *  dois vê (memória "campo obrigatório quebra PR vizinho"). Ler sempre como
   *  `c.paga_na_hora === true`. */
  paga_na_hora?: boolean;
}

/** As formas da compra paga na hora (095) — o que o banco aceita em
 *  registrar_compra_na_hora. Boleto é conta a vencer; fiado, a ver e misto são
 *  da venda. */
export type FormaNaHora = Extract<PaymentMethod, 'dinheiro' | 'pix' | 'debito' | 'credito' | 'transferencia'>;

// --- 22. Fiado / contas a receber (migrações 042 e 065) -----------------------
//
// O erro de modelo mais sério do sistema, consertado em 17/08/2026: a venda
// fiada lançava ENTRADA no caixa, contando como recebido um dinheiro que não
// entrou na gaveta. Agora ela vira dívida; o caixa só a conhece no acerto,
// pela forma com que o cliente realmente pagou.
//
// O SALDO é derivado (valor − recebimentos − abatimentos): fiado se paga em
// pedaços, e cada pedaço é um evento com data, forma e lançamento próprios.
//
// A 065 (27/08/2026) trouxe os pedidos do dono: o acerto pode dizer QUAIS
// PEÇAS cobriu (linhas inteiras de sale_items, cada uma paga uma vez); a
// dívida aceita DESCONTO depois da venda — o acerto com oficina; e a PEÇA
// DEVOLVIDA abate a dívida ("quem deve, se devolver a peça, para de dever
// aquele valor"), antes de virar dinheiro ou vale. Nenhum dos três mexe na
// venda: `sales.total` é o que saiu do estoque, e é dele que vivem comissão,
// meta e margem.

/** Um ACERTO de fiado: a linha de contas_receber_recebimentos (042). Fiado se
 *  paga em pedaços, e cada pedaço é um evento — quanto, quando e como. Só
 *  gerência lê (sel_receber_recs); escrita só por receber_fiado. */
export interface ContaReceberRecebimento {
  id: UUID;
  conta_id: UUID;
  valor: number;
  /** O dia em que o CLIENTE pagou (059), não o dia em que alguém digitou —
   *  coluna `date` ('2026-09-12'): leia com dataSoDiaBR, nunca new Date(). */
  recebido_em: string;
  forma: PaymentMethod;
  cash_movement_id: UUID | null;
  recebido_por: UUID | null;
  recebido_por_nome: string | null;
  observacao: string | null;
  created_at: ISODate;
}

/** Um acerto do fiado, como o documento impresso precisa ver. */
export interface RecebimentoDoFiado {
  valor: number;
  forma: PaymentMethod;
  recebido_em: ISODate;
}

/** O acerto de UMA venda fiada (quitacao_da_venda, 25/08/2026): tudo derivado
 *  dos recebimentos — não existe coluna "quitada" em sales, de propósito
 *  (regra 4: desfazer um recebimento apagaria a linha e a coluna mentiria). */
export interface QuitacaoDaVenda {
  valor: number;
  recebido: number;
  /** Desconto concedido no acerto (065) — sai em linha própria no recibo. */
  desconto: number;
  /** Peças devolvidas que abateram a dívida (065) — linha própria também. */
  devolvido: number;
  saldo: number;
  /** Data do ÚLTIMO acerto; só vira carimbo quando o saldo zera. */
  ultimo_acerto_em: ISODate | null;
  perdido_em: ISODate | null;
  motivo_perda: string | null;
  recebimentos: RecebimentoDoFiado[];
  /** Ids de sale_items já pagos (065) — o recibo marca essas linhas. */
  itens_pagos: UUID[];
}

export interface FiadoRow {
  id: UUID;
  sale_id: UUID;
  /** Nome CONGELADO — de quem era a dívida no dia da venda. */
  cliente: string;
  customer_id: UUID | null;
  valor: number;
  recebido: number;
  /** Descontos concedidos depois da venda (065) — o acerto com oficina. */
  desconto: number;
  /** Peças devolvidas que abateram a dívida (065): quem devolve para de dever. */
  devolvido: number;
  /** valor − recebido − desconto − devolvido. Derivado, nunca armazenado. */
  saldo: number;
  vendida_em: ISODate;
  vencimento: ISODate | null;
  /** Há quantos dias a dívida existe — é o número que ordena a cobrança. */
  dias_em_aberto: number;
  perdido_em: ISODate | null;
  motivo_perda: string | null;
  /**
   * Venda anterior à 042: a entrada já foi (indevidamente) lançada no caixa no
   * dia da venda, então o acerto NÃO lança de novo — senão dobraria.
   */
  caixa_ja_lancado: boolean;
  observacao: string | null;
  vendedor: string | null;
  recebimentos: number;
}

// --- 23. Saúde do negócio (migração 043) --------------------------------------
//
// Os três espelhos de core/sql/functions/saude_negocio.sql — SÓ DONO. As
// decisões de cálculo (o que entra na receita econômica, o corte das despesas
// do EBIT, a anualização do ROIC) estão no cabeçalho daquele arquivo.

export interface ValorDoEstoqueRow {
  valor_custo: number;
  pecas_avaliadas: number;
  /** Saldo positivo SEM custo cadastrado: fora da conta — é a ressalva. */
  pecas_sem_custo: number;
  unidades: number;
}

export interface ResultadoOperacionalRow {
  competencia: string;            // 'YYYY-MM'
  receita: number;                // vendas − devoluções (fiado conta)
  cmv: number;
  /** % da receita com custo conhecido — margem sobre metade precisa dizer. */
  cobertura_cmv_pct: number | null;
  margem_bruta: number;
  margem_bruta_pct: number | null;
  despesas: number;
  ebit: number;
  imposto_pago: number;
  imposto_simulado: number | null;
  resultado_liquido: number;
  margem_liquida_pct: number | null;

  // --- O MESMO MÊS COM O CUSTO DA MERCADORIA NO LUGAR (28/09/2026) -----------
  // As doze de cima são a medição pura, e ficam para auditoria. Estas cinco são
  // a mesma medição com o buraco tapado: o boleto do fornecedor é excluído das
  // despesas de propósito (é estoque, não despesa) e o CMV cadastrado cobre
  // 0,5% das vendas — então a mercadoria não entrava nem como custo nem como
  // despesa, e agosto aparecia com 94% de margem. Todas NULAS sem régua.
  /** O % que o cenário padrão diz que a mercadoria custa. Markup 100% ⇒ 50. */
  custo_regua_pct: number | null;
  /** O custo cadastrado MAIS a régua só sobre a venda que não tem custo. */
  mercadoria: number | null;
  /** Quanto de `mercadoria` é estimativa. 99,5% hoje — a tela avisa. */
  mercadoria_estimada_pct: number | null;
  margem_bruta_real_pct: number | null;
  /** receita − mercadoria − despesas. É este que a tela mostra. */
  ebit_real: number | null;
}

export interface IndicadoresDono {
  estoque_valor: number;
  estoque_pecas_sem_custo: number;
  a_receber: number;
  /** O que se deve HOJE: avulsas em aberto (qualquer vencimento) + fixas em
   *  aberto até o fim do mês corrente no fuso da loja + fixas em aberto já
   *  tocadas (valor real, código de barras ou anexo: o boleto chegou), com
   *  qualquer vencimento. A fixa do mês que vem ainda intocada é fluxo de
   *  caixa, não dívida (19/10/2026, saude_negocio.sql). */
  a_pagar: number;
  divida_liquida: number;
  capital_investido: number | null;
  capital_empregado: number;
  /** Σ `ebit_real` dos 12 meses: o EBIT COM a mercadoria (régua do cenário
   *  padrão sobre a venda sem custo), não a medição pura de 94% de margem.
   *  Null sem régua — nunca o EBIT medido no lugar (19/10/2026,
   *  saude_negocio.sql). */
  ebit_12m: number | null;
  /** Meses com venda — a base da anualização do ROIC. */
  meses_de_historia: number;
  /** ebit_12m ÷ meses_de_historia: a base do "Se eu aplicasse…". Null sem
   *  régua ou sem mês com venda. */
  ebit_medio_mensal: number | null;
  /** EBIT real anualizado ÷ capital empregado. Null sem régua, sem o
   *  capital_investido digitado (decisão 29: vazio, o ROIC não aparece) ou
   *  com capital empregado ≤ 0. */
  roic_pct: number | null;
  /** O CMV MEDIDO (só o custo cadastrado): a base do giro e dos meses de
   *  estoque, que comparam com o estoque também de custo cadastrado. */
  cmv_12m: number;
  giro_estoque: number | null;
  meses_de_estoque: number | null;
  /** Estoque + a receber − a pagar: o que um comprador olharia primeiro. */
  patrimonio: number;
}

// --- 26. Reposição do dia (migração 052) --------------------------------------
// O fechamento do gerente: a lista é DERIVADA das vendas; o que se grava é a
// decisão (X/V + fornecedor) e o item acrescentado à mão.

export type SituacaoReposicao = 'pendente' | 'aprovado' | 'ignorado';

export interface PecaVendidaNoDia {
  /** product_id, ou 'd:<nome>' na peça diversa (que não tem id próprio). */
  chave: string;
  product_id: UUID | null;
  descricao: string;
  sku: string | null;
  /** O código do fabricante DIGITADO no carrinho (063) — null se ninguém
   *  digitou. Vence o do catálogo no pedido e no WhatsApp. */
  codigo_fabricante: string | null;
  /** O código que está no CADASTRO da peça. Null na diversa (não há cadastro)
   *  e null quando o cadastro está sem código — que é justamente quando a
   *  tela oferece gravar lá. */
  codigo_cadastro: string | null;
  /** O código que o vendedor digitou na peça diversa (071). Null na peça
   *  cadastrada e na diversa vendida sem código; dois códigos distintos para
   *  o mesmo nome chegam juntos, separados por ' / '. */
  codigo_balcao: string | null;
  quantidade: number;
  /** As horas de cada saída no dia: '09:12', '11:40'… */
  horas: string[];
  /** Quem vendeu cada saída, na mesma posição de `horas`. */
  vendedores: string[];
  diversa: boolean;
  fornecedor_sugerido_id: UUID | null;
  fornecedor_sugerido_nome: string | null;
  situacao: SituacaoReposicao;
  fornecedor_id: UUID | null;
  fornecedor_nome: string | null;
  /** Já virou pedido — não se redecide por aqui. */
  ja_pedido: boolean;
}

export interface ReposicaoDecisao {
  id: UUID;
  dia: ISODate;
  chave: string;
  product_id: UUID | null;
  descricao: string;
  /** Digitado no carrinho antes de fechar o pedido (063). */
  codigo_fabricante: string | null;
  quantidade: number;
  origem: 'venda' | 'manual';
  situacao: SituacaoReposicao;
  fornecedor_id: UUID | null;
  supplier_order_id: UUID | null;
  decidido_por: UUID | null;
  decidido_por_nome: string | null;
  decidido_em: ISODate | null;
  created_at: ISODate;
  updated_at: ISODate;
}

// --- 26.1 A lista de faxina do cadastro (migração 120) ------------------------
// "Agora faz, pois o foco primordial é fazer vídeos e cadastrar as peças." — o
// dono, 18/10/2026, revertendo o "não" de 27/08. MUTIRÃO continua não haverá
// (08/10): o que existe é esta lista de trabalho, peça a peça.
//
// O ranking NÃO é tabela (regra 4): vem de faxina_familias() e faxina_itens(),
// definer com exige_papel(gerente/dono) no corpo. O que se GRAVA é só a
// decisão humana, em faxina_decisoes.

/** pendente = tocada e deixada para depois; cadastrada = virou peça (com
 *  product_id, exigido pelo CHECK); nao_e_peca = sai para sempre ("ENTREGA",
 *  frete, serviço). SEM LINHA é o estado inicial — ninguém olhou ainda. */
export type SituacaoFaxina = 'pendente' | 'cadastrada' | 'nao_e_peca';

export interface FaxinaDecisao {
  id: UUID;
  /** faxina_chave(texto): minúscula, sem acento, sem pontuação, espaço
   *  colapsado. CONGELADA — mudar aquela função orfana estas linhas. */
  chave: string;
  /** O texto que o gerente viu quando decidiu, como o vendedor digitou. */
  descricao: string;
  situacao: SituacaoFaxina;
  product_id: UUID | null;
  motivo: string | null;
  autor_id: UUID | null;
  /** Congelado, como sales.vendedor. */
  autor_nome: string | null;
  created_at: ISODate;
  updated_at: ISODate;
}

/** Uma linha de faxina_familias(): o primeiro nível da tela, a família da peça
 *  (a primeira palavra — LÂMPADA, PALHETA, FILTRO). É o nível que ordena:
 *  agrupar pelo texto cru dá 2.102 itens de contagem 1 (medido em 18/10). */
export interface FaxinaFamilia {
  familia: string;
  /** Quantas VEZES saiu, somando as três fontes. É por aqui que se ordena —
   *  nunca por unidade (um pacote de grampos pularia à frente do aditivo
   *  vendido 24 vezes) e nunca por dinheiro (faturamento é só do dono). */
  vezes: number;
  vezes_venda: number;
  /** Encomenda ATIVA: cliente esperando agora. */
  vezes_encomenda: number;
  /** Orçamento que não virou venda. */
  vezes_orcamento: number;
  /** A soma das quantidades. Informativa, não ordena. */
  pecas: number;
  /** Quantos nomes distintos há dentro desta família. */
  itens: number;
  ultima: ISODate | null;
  /** Quantas peças ATIVAS do catálogo já começam por esta família. Não é
   *  enfeite: LÂMPADA tem 11 cadastradas e foi digitada à mão 266 vezes —
   *  esconder o número faria cadastrar a décima segunda em vez de perguntar
   *  por que ninguém acha as onze. */
  ja_no_catalogo: number;
}

/** Uma linha de faxina_itens(): o nome exato, com tudo que preenche o
 *  cadastro. */
export interface FaxinaItem {
  chave: string;
  /** Como o vendedor digitou da ÚLTIMA vez — é o que vai congelado na decisão. */
  descricao: string;
  /** O rótulo decorado por ops.normalizar_item (Estúdio): junta 'LAMPADA H-4' e
   *  'lampada h4'. Null quando ela não reconhece o texto — e aí a tela mostra
   *  `descricao`. NÃO corrige erro de digitação: 'lampaca h4' vem separado. */
  termo: string | null;
  tipo_peca: string | null;
  /** Montadora + modelo, da venda (073) ou lido do texto. */
  carro: string | null;
  familia: string;
  vezes: number;
  vezes_venda: number;
  vezes_encomenda: number;
  vezes_orcamento: number;
  pecas: number;
  ultima: ISODate | null;
  /** Os códigos de fabricante digitados no balcão (071). Uso INTERNO: nunca
   *  saem em documento de cliente. Dois códigos para o mesmo nome vêm os dois. */
  codigos: string[] | null;
  /** Quantas peças do catálogo casam com este nome (por conteúdo). */
  ja_no_catalogo: number;
  /** A descrição de UMA delas, para o gerente ver o que já existe antes de
   *  cadastrar em dobro. */
  exemplo_catalogo: string | null;
  /** Null = ninguém decidiu ainda. */
  situacao: SituacaoFaxina | null;
  product_id: UUID | null;
}

// --- 26.2 O vocabulário do balcão na busca (migração 122) -------------------
// "Lâmpada moscão" e "lâmpada pingo" acham a T10: quem busca TERMO também quer
// a peça que tem EQUIVALE. Só ACRESCENTA alternativa à palavra digitada.

export interface VocabularioBusca {
  id: UUID;
  /** UMA palavra na forma de texto_busca(): minúscula, sem acento ('moscao'). */
  termo: string;
  /** Como está no cadastro, na forma de texto_busca() ('t10', 'super branca'). */
  equivale: string;
  observacao: string | null;
  created_at: ISODate;
}

// --- 27. Sugestões de pedido (migração 053) -----------------------------------
// Quem ouve o cliente é quem vende: o balcão sugere a peça e diz POR QUÊ; a
// gerência acata (vira item do fechamento) ou descarta, com devolutiva.

export type SituacaoSugestao = 'aberta' | 'acatada' | 'descartada';

export interface SugestaoPedido {
  id: UUID;
  product_id: UUID | null;
  descricao: string;
  quantidade: number;
  /** Obrigatório: é o que separa informação de compra de lista de desejos. */
  motivo: string;
  vendedor_id: UUID;
  /** Congelado, como sales.vendedor. */
  vendedor_nome: string;
  situacao: SituacaoSugestao;
  resposta: string | null;
  respondido_por: UUID | null;
  respondido_por_nome: string | null;
  respondido_em: ISODate | null;
  created_at: ISODate;
  updated_at: ISODate;
}

/** O placar da impressão (19/08/2026). `parados_ha_10min` é o sinal de agente
 *  fora do ar: documento esperando com ZERO tentativa. */
export interface SaudeDaFila {
  na_fila: number;
  parados_ha_10min: number;
  falhas: number;
  ultimo_enviado_em: ISODate | null;
}

// --- 28. A gaveta do dia (migração 055) ---------------------------------------
// `cash_movements` é o LIVRO (todas as formas); isto é a GAVETA — o dinheiro
// físico que se conta de manhã, usa para troco e confere à noite.

export interface CaixaDoDia {
  dia: ISODate;
  aberto: boolean;
  fundo_inicial: number | null;
  aberto_por_nome: string | null;
  fechado: boolean;
  valor_contado: number | null;
  /** O esperado CONGELADO no fechamento — o retrato daquele momento. */
  valor_esperado_congelado: number | null;
  fechado_por_nome: string | null;
  /** Recalculado agora: fundo + entradas − saídas em dinheiro. */
  esperado_agora: number | null;
  entradas_dinheiro: number;
  saidas_dinheiro: number;
  vendas_a_conferir: number;
  vendas_conferidas: number;
}

// --- 29. O dia da loja (resumo_do_dia, 060) ----------------------------------
// O retrato de UM dia, para o documento impresso que o dono leva para o pai.
// Tudo derivado numa transação só — não existe tabela de resumo.

export interface ResumoDoDia {
  dia: ISODate;
  vendas: { quantidade: number; total: number; canceladas: number; fiado: number };
  por_forma: Array<{ forma: string; quantidade: number; total: number }>;
  por_vendedor: Array<{ vendedor: string; quantidade: number; total: number }>;
  pecas: Array<{ descricao: string; quantidade: number; total: number }>;
  caixa: { entrou: number; saiu: number; entrou_dinheiro: number; saiu_dinheiro: number };
  /** De ONDE veio cada real (vendas, fiado acertado, encomenda, aporte). */
  entradas: Array<{ origem: string; quantidade: number; valor: number }>;
  /** Nula quando o caixa do dia não foi aberto (055). */
  gaveta: {
    fundo: number; aberta_por: string | null; fechada: boolean;
    contado: number | null; esperado: number | null; diferenca: number | null;
    fechada_por: string | null;
  } | null;
  /** Cada saída do dia, contas pagas inclusive (elas lançam saída). */
  saidas: Array<{ categoria: string; descricao: string; valor: number }>;
  encomendas: { novas: number; entregues: number; recebido: number };
  pedidos: Array<{ fornecedor: string; numero: number; itens: number }>;
  orcamentos: { quantidade: number; total: number };
  fiado: { recebido_hoje: number; em_aberto: number };
  a_ver: { emitidos: number; valor: number };
}

/** Uma entrega que ainda não saiu (061) — a rota do entregador. */
export interface EntregaPendente {
  sale_id: UUID;
  codigo: string;
  cliente: string;
  endereco: string | null;
  contato: string | null;
  observacao: string | null;
  /** Quanto a venda custou. Desde 28/09/2026 desce só para GERENTE, DONO e o
   *  VENDEDOR DA PRÓPRIA VENDA — era por aqui que o valor da venda de um colega
   *  aparecia para todo mundo, e num dia de fiado a rota virava a lista dos
   *  valores em aberto da loja. Para o vendedor que não fez a venda vem NULO,
   *  paga ou não. Quem leva a peça decide pelo `pago` abaixo. */
  total: number | null;
  /** Derivado da forma E do acerto: fiado em aberto é o que o entregador vai
   *  cobrar. Desce para TODOS os papéis — é ele que diz se cobra na porta. */
  pago: boolean;
  forma_prevista: PaymentMethod | null;
  troco_para: number | null;
  vendida_em: ISODate;
  vendedor: string | null;
}

/** Uma linha do retorno de pagar_contas (23/08/2026): o lote é atômico e
 *  cada conta sai com o próprio movimento de caixa. */
export interface PagamentoDeLote {
  conta_id: UUID;
  movimento_id: UUID;
  valor_pago: number;
}

// --- 30. Fluxo de caixa (fluxo_de_caixa, 23/08/2026) --------------------------
// A linha do tempo do dinheiro: realizado + previsto. O acumulado é VARIAÇÃO
// na janela — o sistema não conhece saldo bancário, e a tela rotula assim.

export interface FluxoDia {
  dia: ISODate;
  passado: boolean;
  entradas_realizadas: number;
  saidas_realizadas: number;
  entradas_previstas: number;
  saidas_previstas: number;
  /** Contas "a definir" (046) do dia: contadas, nunca somadas. */
  contas_sem_valor: number;
  saldo_dia: number;
  saldo_acumulado: number;
}

// --- 31. Portal B2B (migração 062) -------------------------------------------
// O parceiro é um mundo DISJUNTO da equipe: login GoTrue com linha em
// partner_accounts e nenhuma em profiles. O pedido é DOCUMENTO — nome,
// desconto, sku, descrição, preço e disponibilidade congelam na criação
// (exceção deliberada da regra 4, como quote_items).

export type SituacaoPedidoB2B =
  | 'novo' | 'confirmado' | 'pronto' | 'entregue' | 'cancelado';

/** Registro de intenção, não lançamento de caixa — por isso inclui 'faturado',
 *  que o enum payment_method não tem. A forma REAL entra quando virar venda. */
export type FormaPagamentoB2B =
  | 'pix' | 'dinheiro' | 'debito' | 'credito' | 'boleto' | 'faturado';

export interface PartnerAccount {
  user_id: UUID;
  customer_id: UUID;
  /** Decisão comercial digitada (padrão 10) — o preço final congela no item. */
  desconto_pct: number;
  ativo: boolean;
  criado_por: UUID | null;
  created_at: ISODate;
  updated_at: ISODate;
}

export interface PartnerOrder {
  id: UUID;
  numero: number;
  /** PB-000001 — o que o balcão fala ao telefone. */
  codigo: string;
  customer_id: UUID;
  criado_por_user: UUID | null;
  /** CONGELADO no pedido. */
  cliente_nome: string;
  /** CONGELADO no pedido. */
  desconto_pct: number;
  situacao: SituacaoPedidoB2B;
  forma_pagamento: FormaPagamentoB2B;
  observacao: string | null;
  /** Total do documento — soma dos subtotais na criação. */
  total: number;
  idempotency_key: string | null;
  /** O elo com a venda (V2: converter_pedido_b2b_em_venda). */
  sale_id: UUID | null;
  atualizado_por: UUID | null;
  cancelado_motivo: string | null;
  created_at: ISODate;
  updated_at: ISODate;
}

export interface PartnerOrderItem {
  id: UUID;
  order_id: UUID;
  product_id: UUID | null;
  /** CONGELADO. */
  sku: string;
  /** CONGELADA. */
  descricao: string;
  quantidade: number;
  /** JÁ com o desconto do parceiro (melhor-dos-dois com a promoção). */
  preco_unit: number;
  /** O balcão do momento — o "de R$ X" do papel. */
  preco_tabela: number | null;
  /** true = o estoque cobria a quantidade PEDIDA no momento do pedido. */
  pronta_entrega: boolean;
  ordem: number;
  subtotal: number;
  created_at: ISODate;
}

/** Uma linha de catalogo_b2b: só o que o parceiro pode ver — preço DELE,
 *  cheio para o "de X por Y", disponibilidade como boolean. Nunca quantidade,
 *  local, custo ou fornecedor. */
export interface CatalogoB2BRow {
  product_id: UUID;
  sku: string;
  descricao: string;
  marca: string | null;
  categoria: string | null;
  aplicacoes: string | null;
  tem_foto: boolean;
  preco: number;
  preco_cheio: number;
  pronta_entrega: boolean;
  total_resultados: number;
}

/** Uma linha de resumo_parceiros_b2b (SÓ DONO desde 28/09/2026 — a soma dos
 *  pedidos é o faturamento do canal B2B): derivado, nada armazenado.
 *  Cancelado fica fora da soma — intenção desfeita não é volume. */
export interface ResumoParceiroB2B {
  customer_id: UUID;
  nome: string;
  desconto_pct: number;
  ativo: boolean;
  pedidos: number;
  total: number;
  ultimo_em: ISODate | null;
}

// --- 32. O mural de notas (migração 072) -------------------------------------

/**
 * A paleta da equipe. Oito NOMES, e o hex de cada um mora em
 * `app/src/lib/notas.ts` — o banco guarda a identidade ("Renan é o azul"), a
 * tela decide qual azul se lê no fundo preto.
 *
 * Vermelho não está aqui: é a marca (#D90429) e, nesta tela, o sinal de
 * urgente. Pessoa vermelha faria a coluna inteira dela parecer urgente o dia
 * todo — regra 10 do CLAUDE.md, e o cabeçalho da 072 explica.
 */
export type CorDaEquipe =
  | 'azul' | 'ciano' | 'verde' | 'lima'
  | 'amarelo' | 'laranja' | 'rosa' | 'roxo';

/** Uma linha de `notas` como ela está no banco. A tela usa NotaDoMural, que já
 *  vem com o autor resolvido; esta existe para quem escreve, não para quem lê. */
export interface Nota {
  id: UUID;
  autor_id: UUID;
  texto: string;
  urgente: boolean;
  criada_em: ISODate;
  editada_em: ISODate | null;
  /** Nulo = pendurada na grade. Preenchido = foi para o histórico. */
  finalizada_em: ISODate | null;
  finalizada_por: UUID | null;
}

/**
 * Uma linha de `mural_de_notas()`: a nota com o autor já resolvido — nome e
 * cor não vêm de um join na tela porque o vendedor lê UMA linha de `profiles`,
 * a dele (rls.sql §6). É a RPC definer que atravessa essa fronteira.
 */
export interface NotaDoMural {
  id: UUID;
  autor_id: UUID;
  autor_nome: string;
  autor_cor: CorDaEquipe | null;
  /** false = pessoa desligada com nota ainda pendurada. A gerência baixa. */
  autor_ativo: boolean;
  texto: string;
  urgente: boolean;
  criada_em: ISODate;
  editada_em: ISODate | null;
  finalizada_em: ISODate | null;
  finalizada_por_nome: string | null;
  sou_autor: boolean;
  /** A decisão do dono (072), calculada NO BANCO: autor ou gerência. A tela
   *  desenha o botão a partir disto e não recalcula a regra — as duas pontas
   *  concordarem por construção é o que evita o botão que falha na cara. */
  posso_finalizar: boolean;
}

/** Uma COLUNA da grade (`colaboradores_do_mural()`). Vem mesmo sem nota
 *  nenhuma: a grade é a equipe, não a lista de quem falou hoje. */
export interface ColaboradorDoMural {
  id: UUID;
  nome: string;
  cor: CorDaEquipe | null;
  ativo: boolean;
  notas_penduradas: number;
}

// --- Meu dinheiro: o botão do dono (migração 079) -----------------------------
// O planejamento de cada pessoa NÃO mora aqui: é o banco `pessoal`, com tipos
// próprios em pessoal/types.ts (ADR-008). No banco da loja só existe isto.

/** Tabela `personal_release`: quais papéis podem usar o Meu dinheiro. Só o
 *  dono muda, pela RPC `liberar_meu_dinheiro(papel, liberado)`. */
export interface LiberacaoMeuDinheiro {
  papel: Papel;
  liberado: boolean;
  atualizado_em: ISODate;
  atualizado_por: UUID | null;
}

// --- Planta da loja: o mapa 3D (migração 082) -----------------------------------
// O conteúdo da planta (objetos, cores, medidas) é tipado no app, em
// app/src/lib/loja3d.ts (PlantaDaLoja / ObjetoDaPlanta), que também a valida
// ao ler (lerPlanta): aqui ela é o JSON que o banco guarda.

/** Um código de lugar trocado num salvamento: as peças cujo endereço começava
 *  com `de` passaram a começar com `para`. */
export interface CodigoRenomeado {
  de: string;
  para: string;
  pecas: number;
}

/** De onde veio uma versão da planta (084): `editor` = salvamento do editor;
 *  `sistema` = a planta que vinha no app, guardada no primeiro salvamento;
 *  `volta` = o dono trouxe de volta a versão `restaurada_de`. */
export type OrigemDaPlanta = 'editor' | 'sistema' | 'volta';

/** Tabela `plantas_da_loja`: cada salvamento do editor do mapa. A vigente é a
 *  de `id` maior; vazia = vale o planta.json do app. Escrita só pelas RPCs
 *  `salvar_planta_da_loja(planta, base, renomear, original)` (dono ou gerente)
 *  e `restaurar_planta_da_loja(versao, base)` (só o dono, 084). */
export interface PlantaDaLojaSalva {
  id: number;
  planta: { versao: 1; objetos: unknown[] };
  salva_em: ISODate;
  salva_por: UUID | null;
  salva_por_nome: string | null;
  renomeados: CodigoRenomeado[];
  pecas_renomeadas: number;
  origem: OrigemDaPlanta;
  restaurada_de: number | null;
}

/** Uma linha do histórico do mapa, sem a planta (a lista não precisa dela). */
export type VersaoDaPlanta = Omit<PlantaDaLojaSalva, 'planta' | 'salva_por'>;

/** O que `salvar_planta_da_loja` devolve. */
export interface PlantaSalvaResultado {
  id: number;
  pecas_renomeadas: number;
  renomeados: CodigoRenomeado[];
}

/** O que `restaurar_planta_da_loja` devolve: a linha nova e a versão que ela copiou. */
export interface PlantaRestauradaResultado extends PlantaSalvaResultado {
  restaurada_de: number;
}

/** O que `definir_endereco_da_peca(product_id, local, local_antes)` devolve
 *  (core/sql/functions/endereco_da_peca.sql, 01/10/2026): a peça posta num
 *  lugar do mapa. `de` é o `stock.local` como estava (null = sem lugar ou sem
 *  linha de estoque); `para`, o novo, maiúsculo e sem espaço (null = tirada do
 *  lugar). Dono ou gerente; com `local_antes` diferente do de agora, a RPC
 *  recusa com 40001 ("A peça mudou de lugar…"). */
export interface EnderecoDaPecaResultado {
  product_id: UUID;
  de: string | null;
  para: string | null;
}

// --- O SIMULADOR FINANCEIRO (migração 085) -----------------------------------
//     As premissas que o dono digita, e a base que o banco mede.

/** `cenarios_financeiros.ferias_provisiona` (088, texto com CHECK).
 *  - `terco`: só o terço constitucional — o salário do mês de férias já sai na
 *    folha daquele mês, então guardá-lo também é contar duas vezes;
 *  - `salario_e_terco`: a remuneração inteira, como a contabilidade provisiona. */
export type FeriasProvisiona = 'terco' | 'salario_e_terco';

/** Tabela `cenarios_financeiros`: uma linha por cenário (Base, Aperto, Folga).
 *  Decisões DIGITADAS, no molde de `products.margem_alvo` — nunca número
 *  derivado. **Nulo significa "não informado"**, e a tela mostra o vazio e diz
 *  o que falta, em vez de inventar. Só o dono lê e escreve (rls §22).
 *
 *  `capital_investido` e `aliquota_imposto_pct` NÃO vivem aqui: são da loja e
 *  moram em `limites_operacao` (043). `aliquota_venda_pct` abaixo é só o
 *  "e se fosse outra?" deste cenário. */
export interface CenarioFinanceiro {
  id: number;
  nome: string;
  descricao: string | null;
  /** O cenário que a tela abre sozinha. Um só, garantido por índice único. */
  padrao: boolean;

  /** Venda esperada por DIA DE LOJA (não por dia do calendário). */
  venda_por_dia: number | null;
  crescimento_mes_pct: number | null;
  dias_de_loja_padrao: number | null;

  /** Custo da mercadoria como % da venda. Markup 2x = 50. */
  custo_mercadoria_pct: number | null;
  /** Compra de mercadoria que não passa por boleto e não deixa rastro. */
  compra_a_vista_mes: number | null;

  folha_mensal: number | null;
  /** INSS + FGTS + dívida da PGFN sobre a folha, em %. */
  encargo_pct: number | null;
  /** **OBSOLETA desde a 088: ninguém lê.** A provisão de 13º e férias sai da
   *  folha pela regra do motor (`provisaoDaFolha`). A coluna fica porque o seed
   *  da 085 a cita e o APPLY reexecuta a 085. */
  decimo_e_ferias_mes: number | null;
  /** Quanto da folha é pró-labore dos sócios (088). Sai da base da provisão de
   *  13º e férias quando `decimo_dos_donos` não é true. Nulo = não informado:
   *  a provisão conta a folha inteira e a tela diz. */
  pro_labore_mensal: number | null;
  /** Os donos recebem 13º e férias? (088) Nulo = não respondido: o motor supõe
   *  que não (pró-labore não tem por lei) e diz que supôs. */
  decimo_dos_donos: boolean | null;
  /** O que a provisão de férias guarda (088). Nulo = supõe o terço. */
  ferias_provisiona: FeriasProvisiona | null;

  /** Nulo = usa `limites_operacao.aliquota_imposto_pct` (043). */
  aliquota_venda_pct: number | null;
  taxa_debito_pct: number | null;
  taxa_credito_pct: number | null;
  prazo_cartao_dias: number | null;
  /** O que sai do caixa e não é da operação (categoria `retirada`, 081). */
  retirada_mensal: number | null;

  created_at: ISODate;
  criado_por: UUID | null;
  updated_at: ISODate;
}

/** Tabela `cenario_meses`: a exceção de UM mês dentro de um cenário. Linha
 *  existe só para dizer o que MUDA — nulo em qualquer coluna é "segue o
 *  cenário". Mesma divisão regra/ocorrência da folha (083). */
export interface CenarioMes {
  id: number;
  cenario_id: number;
  /** Sempre o dia 1, como a competência da folha. */
  competencia: string;
  /** Zero é válido (mês fechado); nulo é "não disse". */
  dias_de_loja: number | null;
  venda_por_dia_override: number | null;
  entrada_extra: number | null;
  saida_extra: number | null;
  observacao: string | null;
}

/** Uma faixa da venda por dia da semana: a FORMA da receita. Segunda não é
 *  igual a sábado, e é por isso que projetar pela média do mês erra. */
export interface VendaPorDiaDaSemana {
  /** ISO: 1 = segunda … 7 = domingo. */
  dia_semana: number;
  nome: string;
  /** Quantos dias desse dia da semana tiveram venda na janela. */
  dias: number;
  media: number;
  total: number;
}

/** O que `base_da_simulacao(p_meses)` devolve: o MEDIDO, com a qualidade dele
 *  ao lado. Uma chamada só, de propósito — ela é cara (a DRE varre a história
 *  inteira), então a tela guarda e simula no aparelho.
 *
 *  **Hoje fica fora das médias**: o dia está pela metade. */
export interface BaseDaSimulacao {
  hoje: string;
  desde: string;
  janela_meses: number;

  venda: {
    por_dia_da_semana: VendaPorDiaDaSemana[];
    media_por_dia_de_loja: number | null;
    ticket_medio: number | null;
    /** Dias que de fato tiveram venda — o denominador honesto. */
    dias_de_loja_medidos: number;
    por_mes: {
      competencia: string;
      dias_de_loja: number;
      receita: number;
      vendas: number;
      por_dia: number | null;
    }[];
  };

  /** A venda `misto` (075) entra pelas PARTES dela, senão a maquininha
   *  desaparece dentro de um rótulo só. */
  recebimento: { forma: string; valor: number; pct: number | null }[];

  /** O realizado como a `resultado_operacional()` o conta — sem segunda conta
   *  da mesma coisa. `cobertura_cmv_pct` é a ressalva que não se esconde. */
  dre: ResultadoOperacionalRow[];

  contas: {
    por_mes_categoria: {
      competencia: string;
      categoria: string;
      valor: number;
      titulos: number;
      /** Ainda sem valor_real: é estimativa, e a tela mostra com ~. */
      estimados: number;
    }[];
    /** `retirada` (081) vem rotulada, nunca somada junto: não é da loja. */
    retirada_por_mes: { competencia: string; valor: number; titulos: number }[];
  };

  compras: {
    /** As contas de fornecedor por mês de vencimento, pagas ou não — e desde a
     *  095 também a compra paga NA HORA, que é mercadoria já lançada no mês. */
    boleto_por_mes: { competencia: string; valor: number; titulos: number }[];
    /** Do CADASTRO ao vencimento — **não** é o prazo do fornecedor (PMP). A
     *  conta não guarda a data do documento; o PMP nasce na etapa H. Sem a
     *  compra paga na hora (095): prazo zero dela derrubaria a média. */
    prazo_cadastro_vencimento_dias: number | null;
    /** Os títulos da média acima — também sem a compra paga na hora. */
    titulos_no_periodo: number;
  };

  folha: {
    /** Soma dos salários de quem está ativo. Comissão fica fora: ela depende
     *  da venda, e quem a projeta é o simulador. */
    combinada_salario_base: number | null;
    pessoas_ativas: number;
    /** O dia de cada pessoa (083) e quanto cai nele. */
    dias_de_pagamento: { dia: number; valor: number }[];
    lancada_por_mes: {
      competencia: string;
      total: number;
      pago: number | null;
      pessoas: number;
    }[];
  };

  fiado: {
    aberto: number;
    titulos: number;
    prazo_recebimento_dias: number | null;
    acertos_no_periodo: number;
  };

  estoque: {
    a_custo: number | null;
    pecas_avaliadas: number;
    /** Saldo positivo sem custo cadastrado: FORA da conta, e é a ressalva. */
    pecas_sem_custo: number;
    unidades: number | null;
  };

  /** O último DAS (Simples Nacional) e a venda do mês a que ele se refere. A
   *  alíquota não vem pronta: o motor divide e diz quando o mês está
   *  incompleto no sistema. Nulo quando não há DAS vencido lançado. */
  imposto: {
    ultimo_das: {
      vencimento: string;
      valor: number;
      /** 'AAAA-MM' — o mês anterior ao vencimento. */
      competencia: string;
      /** Venda bruta daquele mês no sistema. Nula se não houve venda. */
      receita_da_competencia: number | null;
      /** Quantos dias daquele mês o sistema viu com venda. */
      dias_com_venda: number;
      /** Quantos dias de loja (sem domingo) o mês tem no calendário. */
      dias_de_loja_no_calendario: number;
    } | null;
    /** Todo DAS lançado (o do mês e o de parcelamento), por mês de
     *  vencimento — o mesmo recorte de `contas.por_mes_categoria`. É o
     *  pagamento do imposto que a DRE já reconhece por alíquota; o motor tira
     *  isto das despesas para não contar duas vezes. */
    das_por_mes: { competencia: string; valor: number }[];
  };

  /** O cartão (087): a taxa que as vendas congelaram, ponderada pelo valor,
   *  e a tabela vigente da adquirente. Venda `misto` fica fora do medido. */
  cartao: {
    medido: {
      modalidade: 'debito' | 'credito';
      vendas: number;
      valor: number;
      taxa_media_pct: number | null;
    }[];
    tabela: {
      bandeira: BandeiraCartao;
      modalidade: 'debito' | 'credito';
      parcelas_de: number;
      parcelas_ate: number;
      taxa_pct: number;
    }[];
  };

  /** As duas configurações da loja (043), que o cenário lê e não duplica.
   *  Nulas hoje as duas: sem capital não sai ROIC, sem alíquota não sai a
   *  linha de imposto. */
  limites: {
    capital_investido: number | null;
    aliquota_imposto_pct: number | null;
  };

  cenarios: CenarioFinanceiro[];
}

// --- O LOTE DE DOCUMENTOS (migração 086) -------------------------------------
//     Cadastrar as contas de um PDF de uma vez, e poder voltar atrás.

/** Uma importação de PDF do dia. Sem ela, "as contas deste PDF" não tem
 *  identidade — `created_at` por linha se mistura quando o dono digitaliza em
 *  duas levas no mesmo minuto. */
export interface LoteDeDocumentos {
  id: number;
  arquivo: string;
  paginas: number;
  criado_em: ISODate;
  /** Congelado, como o autor da versão da venda (070). */
  criado_por_nome: string | null;
  /** Os três números são SOMADOS por `historico_de_lotes()`, não gravados no
   *  lote (regra 4): contador congelado passa a mentir na primeira conta
   *  cancelada ou excluída da leva. */
  contas: number;
  /** O que a leva ainda custa: soma só das não canceladas. */
  total: number;
  cancelado_em: ISODate | null;
  cancelado_por_nome: string | null;
  /** O que ainda está de pé — é o que decide se o botão de cancelar aparece. */
  contas_vivas: number;
  contas_pagas: number;
}

/** O que o dono espera vender em CADA dia da semana, por cenário (091).
 *
 *  Tabela de EXCEÇÃO: a linha existe só para o dia que ele digitou. Dia sem
 *  linha continua saindo do escalar `venda_por_dia` espalhado pelos pesos
 *  medidos — ausência é ausência, não zero.
 *
 *  ZERO é resposta válida e quer dizer "não abro neste dia". A diferença entre
 *  zero e ausente é o que o motor usa para decidir se o dia abre. */
export interface CenarioDiaDaSemana {
  id: number;
  cenario_id: number;
  /** 1 = segunda … 7 = domingo, a mesma numeração de `venda.por_dia_da_semana`. */
  dia_semana: number;
  venda_por_dia: number;
}

/** Uma folha do PDF virando conta, na chamada de `lancar_lote`. */
export interface ContaDoLote {
  descricao: string;
  categoria: CategoriaConta;
  valor: number | null;
  vencimento: string;
  fornecedor_id?: string | null;
  /** A linha digitável, quando a folha tinha código de barras legível. */
  codigo?: string | null;
}

/** O que `cancelar_lote` devolve: uma linha por conta, e o porquê de cada
 *  recusa. Cancelar não é silencioso — conta paga e ocorrência de conta fixa
 *  ficam de pé, e o dono precisa saber quais. */
export interface ResultadoDoCancelamento {
  conta_id: string;
  descricao: string;
  cancelada: boolean;
  porque: string | null;
}

// --- Estúdio (schemas privados video/ops/mkt/sec, migrações 100+) -----------
// Num arquivo à parte para não misturar com o modelo da loja; mesma regra 1:1.
export * from './types-estudio';
